/**
 * One durable, versioned boundary around Pathways' research artefacts.
 *
 * The feature modules remain the authorities for their own arithmetic. This file
 * gives them a common persistence shape, a bounded import codec, and immutable
 * collection updates. Import never calls a model. It rebuilds coverage append
 * order, contradiction verdicts and falsification recommendations with the
 * deterministic functions that own those conclusions; invalid signed semantic
 * records are rejected by their existing cryptographic verifiers.
 */

import { appendCoverageRound, createCoverageTrace } from './coverage'
import { normalizeScopeFieldOrigins } from './evidenceContinuity'
import { parseAgenda, type InvestigationAgenda } from './agenda'
import { parseRunAssessments, type RunAssessmentWorkspace } from './runAssessment'
import { parseMathematicalResearch, type MathematicalResearch } from './mathematics'
import { parseExperimentWorkspace, type ExperimentWorkspaceState } from './experiments'
import {
  parseComputationalWorkspace,
  type ComputationalWorkspaceState,
} from './computationalWorkspace'
import {
  CONTRADICTION_METHOD,
  CONTRADICTION_VERDICTS,
  LEGACY_CONTRADICTION_METHOD,
  NULL_MEANINGS,
  completeContradictionAnalysis,
  emptyClaimScope,
  normalizeQuantitativeFinding,
  type CausalMode,
  type ClaimDirection,
  type ClaimScope,
  type ContradictionAnalysis,
  type DiscriminatingTest,
  type FutureBearing,
  type ScopedClaim,
  type TestPrediction,
} from './contradiction'
import {
  CORE_REFUTATION_MIN,
  CORE_REFUTATION_UNCHECKED_MIN,
  DEFAULT_EVIDENCE_PROFILE,
  MANUAL_BINDABLE_EVIDENCE_ORIGINS,
  normalizeEvidenceProfile,
} from './epistemic'
import {
  completeFalsificationPortfolio,
  resolveFalsificationPortfolio,
  type FalsificationDecisionEffect,
  type FalsificationFuture,
  type FalsificationPortfolio,
} from './falsification'
import {
  EVIDENCE_ORIGINS,
  buildEvidenceGenealogy,
  type EvidenceRecord,
  type GenealogyOverride,
} from './genealogy'
import { manualEvidenceBindingId } from './evidenceIds'
import {
  KNOWLEDGE_REVISION_VERSION,
  MAX_KNOWLEDGE_INPUT_REVISIONS,
  MAX_KNOWLEDGE_REVISIONS,
  normalizeKnowledgeHistory,
  resolveKnowledgeRows,
  type KnowledgeDelta,
  type KnowledgeHistory,
  type KnowledgeRevision,
} from './knowledgeHistory'
import {
  BRANCH_LINEAGE_VERSION,
  CRITIQUE_ALGORITHM,
  CRITIQUE_VERSION,
  SEMANTIC_COMMIT_VERSION,
  SEMANTIC_SNAPSHOT_VERSION,
  SEMANTIC_WORKSPACE_VERSION,
  canonicalSemanticJson,
  critiqueBindingCheck,
  semanticBranchHead,
  verifyCritique,
  verifySemanticCommitLog,
  type BranchLineage,
  type CritiqueFinding,
  type CritiquePayload,
  type PersonActor,
  type SemanticActor,
  type SemanticCommit,
  type SemanticSnapshot,
  type SemanticWorkspace,
  type SignedCritiqueEnvelope,
} from './semanticGit'
import type {
  ClaimType,
  EvidenceReach,
  EvidenceRuleProfile,
  EvidenceSourceClass,
  EvidenceStance,
  ManualBindableEvidenceOrigin,
  ManualEvidenceContentScope,
  StudyDesign,
} from './epistemic'
import type {
  AdmissionDecision,
  CoverageProtocol,
  CoverageResult,
  CoverageRoundDraft,
  CoverageTrace,
  RequiredCoverageCombination,
  ResultReach,
  RoundCompletion,
} from './coverage'
import type {
  CalibrationTournament,
  ReasoningErrorKind,
  TournamentRound,
  TournamentStage,
  TournamentSubmission,
} from './tournament'
import { storableId } from './types'
import type { ModelStamp, NodeOrigin } from './types'
import { canonicalUrl, isHttpUrl } from './url'

export const RESEARCH_STATE_KIND = 'pathways-research-state' as const
export const RESEARCH_STATE_VERSION = 1 as const
export const RESEARCH_AUDIT_RECEIPT_VERSION = 1 as const
export const MANUAL_EVIDENCE_BINDING_VERSION = 1 as const

export { MANUAL_BINDABLE_EVIDENCE_ORIGINS } from './epistemic'
export type { ManualBindableEvidenceOrigin, ManualEvidenceContentScope } from './epistemic'

/**
 * Persistence limits are part of the codec contract rather than implementation
 * trivia. They stop one imported document from turning startup into an unbounded
 * traversal while leaving room for research projects much larger than the UI.
 */
export const RESEARCH_STATE_LIMITS = {
  idChars: 240,
  labelChars: 1_000,
  textChars: 120_000,
  promptChars: 160_000,
  listItems: 512,
  evidenceRecords: 5_000,
  manualEvidenceBindings: 10_000,
  genealogyOverrides: 5_000,
  coverageTraces: 128,
  coverageRoundsPerTrace: 1_000,
  coverageResultsPerRound: 2_000,
  evidenceAnnotations: 10_000,
  claimAnnotations: 10_000,
  decisionCriterionSignals: 512,
  claimScopes: 10_000,
  contradictionAnalyses: 2_000,
  falsificationPortfolios: 2_000,
  calibrationTournaments: 512,
  semanticCommits: 5_000,
  branchLineages: 1_000,
  signedCritiques: 2_000,
  knowledgeRevisions: MAX_KNOWLEDGE_REVISIONS,
  knowledgeRevisionInputs: MAX_KNOWLEDGE_INPUT_REVISIONS,
  /**
   * A retained board, or the delta after one, is parsed EXACTLY or refused. An
   * entry the general caps would drop from a delta is a change every later board
   * would show undone, so these are sized to the exploration a board mirrors —
   * the session codec caps no node count — rather than to a list a surface
   * reads. Exact board strings use the larger retained-prompt ceiling; clipping
   * a prompt can otherwise remove a field origin while preserving its offsets.
   */
  knowledgeBoardEntries: 50_000,
  knowledgeBoardJsonNodes: 4_000_000,
  /**
   * A semantic snapshot is parsed exactly or refused, for the reason above and
   * one more. It is not a mirror of an exploration, it IS one — its `nodes` and
   * `bridges` are keyed by id — and `buildMergeRequest` hashes the whole of it.
   * Read under the general caps, an exploration past 512 nodes came back
   * silently short, and the hash recomputed over the short copy disagreed with
   * the hash the app itself had just written: the importer refused its own
   * export as a snapshot mismatch. Sized like the board's and named separately,
   * because a limit that moves for a board should not move an exploration.
   */
  snapshotEntries: 50_000,
  snapshotJsonNodes: 4_000_000,
  auditReceipts: 5_000,
  promptsPerReceipt: 64,
  rawResultsPerReceipt: 5_000,
  citationsPerReceipt: 5_000,
  snapshotsPerReceipt: 5_000,
  jsonDepth: 12,
  jsonNodes: 50_000,
} as const

export type JsonValue =
  null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue }

export type StoredCoverageTrace = CoverageTrace<JsonValue, JsonValue, JsonValue>

export interface ResearchClaimScope {
  nodeId: string
  scope: ClaimScope
  updatedAt: number
  opId?: string
  model?: ModelStamp
}

export interface EvidenceAnnotation {
  /** Evidence-record annotations classify a source; binding annotations classify one claim/source use. */
  key: { kind: 'evidence-record'; id: string } | { kind: 'binding'; id: string }
  sourceClass?: EvidenceSourceClass
  studyDesign?: StudyDesign
  populationMatch?: boolean | null
  timeframeMatch?: boolean | null
  updatedAt: number
  opId?: string
  model?: ModelStamp
}

/**
 * One user-authored join between a claim and a structured, locally recorded
 * artefact. A URL or DOI on that artefact remains identity metadata; this row
 * never claims retrieval returned it.
 */
export interface ManualEvidenceBinding {
  version: typeof MANUAL_EVIDENCE_BINDING_VERSION
  id: string
  nodeId: string
  recordId: string
  stance: EvidenceStance
  contentScope: ManualEvidenceContentScope
  createdAt: number
  updatedAt: number
}

/** A person-authored claim class. Absence is compiled as `general`, never inferred from prose. */
export interface ResearchClaimAnnotation {
  nodeId: string
  claimType: ClaimType
  updatedAt: number
  opId?: string
  model?: ModelStamp
}

/**
 * A structured, user-recorded decision-criterion signal.
 *
 * The criterion text identifies the standing brief it belongs to. The run loop
 * reads only `status`; it never asks a model whether the prose sounds satisfied.
 */
export interface ResearchDecisionCriterionSignal {
  id: string
  criterion: string
  status: 'met' | 'not-met'
  updatedAt: number
}

export type ResearchAuditKind =
  'reason' | 'search' | 'source-check' | 'coverage' | 'panel' | 'chat' | 'semantic'

export interface ResearchPromptReceipt {
  id: string
  role: 'system' | 'developer' | 'user' | 'assistant' | 'tool'
  content: string
}

/** Exactly what one retrieval surface returned before citation admission. */
export interface RawSearchResultReceipt {
  id: string
  queryId: string
  retrievalSourceId: string
  rank: number | null
  /** Stable cross-surface identity, including for a URL-less local passage. */
  identity: string
  /** Stable audit locator. It may be an internal locator rather than an HTTP URL. */
  locator: string
  url: string | null
  title: string
  snippet?: string
  returnedAt: number
  sourceSnapshotHash?: string
  /** Provider payload retained as bounded JSON and never interpreted here. */
  raw?: JsonValue
}

export interface CitationAuditReceipt {
  id: string
  rawResultId: string | null
  url: string
  title: string
  decision: 'admitted' | 'rejected'
  rule: string
  reason: string
  evidenceRecordId?: string
  sourceSnapshotHash?: string
}

export interface SourceSnapshotReceipt {
  id: string
  identity: string
  locator: string
  url: string | null
  capturedAt: number
  sha256: string
  bytes: number | null
}

export interface ResearchUsageReceipt {
  inputTokens: number
  outputTokens: number
  /** Null means the provider did not expose this category. */
  reasoningTokens: number | null
  /** Null means the input total was reported without a cache breakdown. */
  cacheReadInputTokens: number | null
  /** Null means the input total was reported without a cache breakdown. */
  cacheWriteInputTokens: number | null
  searchRequests: number
  /** An estimate that leans high, never a bill or a total. */
  estimatedUsd: number | null
}

/**
 * How the operation surrounding one audit receipt ended.
 *
 * Optional only for v1 receipts written before operation outcomes were recorded.
 * New producers always supply it; the codec never upgrades an unknown legacy
 * outcome into "completed" because absence and success are different facts.
 */
export type ResearchAuditOutcome =
  { kind: 'completed' } | { kind: 'refused' | 'cancelled' | 'failed'; code: string; reason: string }

export interface ResearchAuditReceipt {
  version: typeof RESEARCH_AUDIT_RECEIPT_VERSION
  id: string
  opId: string
  kind: ResearchAuditKind
  model: ModelStamp
  prompts: ResearchPromptReceipt[]
  startedAt: number
  completedAt: number
  usage: ResearchUsageReceipt
  outcome?: ResearchAuditOutcome
  rawSearchResults: RawSearchResultReceipt[]
  citations: CitationAuditReceipt[]
  sourceSnapshots: SourceSnapshotReceipt[]
}

export interface ResearchState {
  kind: typeof RESEARCH_STATE_KIND
  version: typeof RESEARCH_STATE_VERSION
  id: string
  createdAt: number
  updatedAt: number
  evidenceProfile: EvidenceRuleProfile
  evidenceRecords: EvidenceRecord[]
  manualEvidenceBindings: ManualEvidenceBinding[]
  genealogyOverrides: GenealogyOverride[]
  coverageTraces: StoredCoverageTrace[]
  evidenceAnnotations: EvidenceAnnotation[]
  claimAnnotations: ResearchClaimAnnotation[]
  decisionCriterionSignals: ResearchDecisionCriterionSignal[]
  claimScopes: ResearchClaimScope[]
  contradictionAnalyses: ContradictionAnalysis[]
  falsificationPortfolios: FalsificationPortfolio[]
  calibrationTournaments: CalibrationTournament[]
  semanticCommits: SemanticCommit[]
  branchLineages: BranchLineage[]
  /** Null is the safe legacy state: a branch must be checked out before it can extend a log. */
  semanticWorkspace: SemanticWorkspace | null
  signedCritiques: SignedCritiqueEnvelope[]
  /** Automatic whole-board states for read-only "as known" replay: one whole
   *  board and the changes after it, read back through `knowledgeBoards`. */
  knowledgeHistory: KnowledgeHistory
  auditReceipts: ResearchAuditReceipt[]
  /** Optional execution queue; attempts survive undo and are never automatic retries. */
  agenda?: InvestigationAgenda
  /** Bounded local calculation draft/result; excluded from semantic argument history. */
  computationalWorkspace?: ComputationalWorkspaceState
  /** Execution records survive undo; imported results never grant source admission. */
  experiments?: ExperimentWorkspaceState
  mathematics?: MathematicalResearch
  /** Malformed mathematical dependencies must never disappear into an ordinary run. */
  mathematicsInvalid?: true
  /** Paid reviews retain their frozen inputs and unfinished attempts across reload. */
  assessments?: RunAssessmentWorkspace
}

export type ResearchStateDiagnosticCode =
  | 'invalid-root'
  | 'unsupported-version'
  | 'field-defaulted'
  | 'entry-dropped'
  | 'limit-applied'
  | 'citation-gate'
  | 'semantic-log-rejected'
  | 'critique-rejected'

export interface ResearchStateDiagnostic {
  code: ResearchStateDiagnosticCode
  path: string
  message: string
}

export interface ResearchStateImportResult {
  state: ResearchState
  diagnostics: ResearchStateDiagnostic[]
  /** False only when the root or its version was refused wholesale. */
  accepted: boolean
}

export interface StructurallySanitizedResearchState extends ResearchStateImportResult {
  /** Structural import cannot authenticate signed records synchronously. */
  cryptographicRecordsVerified: false
}

export interface SanitizedResearchState extends ResearchStateImportResult {
  /** Every retained commit log and signed critique passed its existing verifier. */
  cryptographicRecordsVerified: true
}

export interface CreateResearchStateInput {
  id?: string
  now?: number
  evidenceProfile?: EvidenceRuleProfile
}

export interface SanitizeResearchStateOptions {
  fallbackId?: string
  now?: number
}

export interface ResearchEntityMap {
  evidenceRecords: EvidenceRecord
  manualEvidenceBindings: ManualEvidenceBinding
  genealogyOverrides: GenealogyOverride
  coverageTraces: StoredCoverageTrace
  evidenceAnnotations: EvidenceAnnotation
  claimAnnotations: ResearchClaimAnnotation
  decisionCriterionSignals: ResearchDecisionCriterionSignal
  claimScopes: ResearchClaimScope
  contradictionAnalyses: ContradictionAnalysis
  falsificationPortfolios: FalsificationPortfolio
  calibrationTournaments: CalibrationTournament
  semanticCommits: SemanticCommit
  branchLineages: BranchLineage
  signedCritiques: SignedCritiqueEnvelope
  auditReceipts: ResearchAuditReceipt
}

export type ResearchCollection = keyof ResearchEntityMap

const INVALID = Symbol('invalid-json')
type Invalid = typeof INVALID
type DiagnosticSink = (diagnostic: ResearchStateDiagnostic) => void

const SOURCE_CLASSES: readonly EvidenceSourceClass[] = [
  'peer-reviewed-primary',
  'preprint',
  'systematic-review',
  'registry',
  'dataset',
  'government',
  'policy',
  'standards',
  'news',
  'web-primary',
  'web-secondary',
  'internal-document',
  'recorded-outcome',
  'unknown',
]
const EVIDENCE_REACHES: readonly EvidenceReach[] = [
  'full-text',
  'verified-quote',
  'abstract-only',
  'snippet',
  'metadata-only',
  'unreadable',
]
const STUDY_DESIGNS: readonly StudyDesign[] = [
  'randomized-trial',
  'controlled-experiment',
  'quasi-experiment',
  'cohort',
  'case-control',
  'cross-sectional',
  'case-report',
  'systematic-review',
  'meta-analysis',
  'registry-analysis',
  'simulation',
  'qualitative',
  'official-record',
  'unknown',
]
const CLAIM_TYPES: readonly ClaimType[] = [
  'causal',
  'comparative',
  'descriptive',
  'mechanistic',
  'forecast',
  'safety',
  'novelty',
  'general',
]
const EVIDENCE_STANCES: readonly EvidenceStance[] = ['supports', 'disputes', 'context']
const MANUAL_CONTENT_SCOPES: readonly ManualEvidenceContentScope[] = [
  'full-text',
  'abstract-only',
  'metadata-only',
]
const RESULT_REACHES: readonly ResultReach[] = [
  'full-text',
  'abstract-only',
  'snippet',
  'blocked',
  'unreadable',
  'unsearched',
]
const DIRECTIONS: readonly ClaimDirection[] = [
  'positive',
  'negative',
  'null',
  'mixed',
  'unspecified',
]
const CAUSAL_MODES: readonly CausalMode[] = [
  'causal',
  'correlational',
  'descriptive',
  'unspecified',
]
const FUTURE_BEARINGS: readonly FutureBearing[] = ['positive', 'negative', 'inconclusive']
const TOURNAMENT_STAGES: readonly TournamentStage[] = [
  'blind-position',
  'cross-examination',
  'rebuttal',
]
const NODE_ORIGINS: readonly NodeOrigin[] = [
  'expand',
  'deepen',
  'challenge',
  'diverge',
  'experiment',
  'invert',
  'bridge',
  'revise',
  'outcome',
  'rebase',
  'conjecture',
  'panel',
  'audit',
  'seed',
  'manual',
]
const ERROR_KINDS: readonly ReasoningErrorKind[] = [
  'confident-but-refuted',
  'dismissed-but-supported',
  'citation-rejected',
  'independence-overcount',
  'scope-mismatch',
  'stale-dependency',
  'operational-failure',
]
const AUDIT_KINDS: readonly ResearchAuditKind[] = [
  'reason',
  'search',
  'source-check',
  'coverage',
  'panel',
  'chat',
  'semantic',
]
const AUDIT_OUTCOME_KINDS: readonly Exclude<ResearchAuditOutcome['kind'], 'completed'>[] = [
  'refused',
  'cancelled',
  'failed',
]
const PROMPT_ROLES: readonly ResearchPromptReceipt['role'][] = [
  'system',
  'developer',
  'user',
  'assistant',
  'tool',
]
const PROVIDERS: readonly ModelStamp['provider'][] = [
  'anthropic',
  'openai',
  'google',
  'custom',
  'typesafe',
]
const HASH = /^[0-9a-f]{64}$/i

const object = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const oneOf = <T extends string>(value: unknown, values: readonly T[]): value is T =>
  typeof value === 'string' && (values as readonly string[]).includes(value)

const text = (value: unknown, maximum: number = RESEARCH_STATE_LIMITS.textChars): string | null =>
  typeof value === 'string' ? value.trim().slice(0, maximum) : null

const requiredText = (
  value: unknown,
  maximum: number = RESEARCH_STATE_LIMITS.labelChars,
): string | null => {
  const held = text(value, maximum)
  return held ? held : null
}

const time = (value: unknown, fallback: number = 0): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback

const nonnegative = (value: unknown, fallback: number = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : fallback

const integer = (value: unknown, fallback: number = 0): number =>
  Math.trunc(nonnegative(value, fallback))

const nullableNonnegative = (value: unknown): number | null =>
  value === null
    ? null
    : typeof value === 'number' && Number.isFinite(value)
      ? Math.max(0, value)
      : null

const nullableInteger = (value: unknown): number | null => {
  const held = nullableNonnegative(value)
  return held === null ? null : Math.trunc(held)
}

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback

const lexical = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const clone = <T>(value: T): T => JSON.parse(canonicalSemanticJson(value)) as T

function boundedRows(
  value: unknown,
  maximum: number,
  path: string,
  diagnostic: DiagnosticSink,
): unknown[] {
  if (!Array.isArray(value)) {
    if (value !== undefined) {
      diagnostic({ code: 'field-defaulted', path, message: 'Expected an array; used empty.' })
    }
    return []
  }
  if (value.length > maximum) {
    diagnostic({
      code: 'limit-applied',
      path,
      message: `Kept the first ${maximum} entries and dropped the rest.`,
    })
  }
  return value.slice(0, maximum)
}

/**
 * A list whose order is its meaning: a protocol's steps, an argument as it was
 * made. A repeat is dropped at its later occurrence, as the engine's parser
 * already drops it, and nothing is sorted. The sorting below printed a
 * procedure as "Fit…, Obtain…, Prepare…".
 */
function orderedStrings(
  value: unknown,
  maximum: number = RESEARCH_STATE_LIMITS.listItems,
): string[] {
  if (!Array.isArray(value)) return []
  return uniqueInOrder(
    value.slice(0, maximum).flatMap((row) => {
      const held = requiredText(row, RESEARCH_STATE_LIMITS.labelChars)
      return held ? [held] : []
    }),
    (row) => row,
  )
}

/** A set — identifiers, labels, rule names — kept in one canonical order. */
function strings(value: unknown, maximum: number = RESEARCH_STATE_LIMITS.listItems): string[] {
  return orderedStrings(value, maximum).sort(lexical)
}

/**
 * How far one value may reach, and what happens at the edge. The general codec
 * clips a list and drops what is over budget, which bounds a list a surface reads
 * honestly. A ledger row is parsed exactly or refused, because a clipped delta is
 * a corrupted chain: the entry it lost is a change every later board would show
 * undone, and nothing on screen would say so.
 */
interface JsonBounds {
  listItems: number
  exact: boolean
}
const GENERAL_BOUNDS: JsonBounds = { listItems: RESEARCH_STATE_LIMITS.listItems, exact: false }
const BOARD_BOUNDS: JsonBounds = {
  listItems: RESEARCH_STATE_LIMITS.knowledgeBoardEntries,
  exact: true,
}
const SNAPSHOT_BOUNDS: JsonBounds = {
  listItems: RESEARCH_STATE_LIMITS.snapshotEntries,
  exact: true,
}

function jsonValue(
  value: unknown,
  budget: { nodes: number } = { nodes: RESEARCH_STATE_LIMITS.jsonNodes },
  depth: number = 0,
  stack = new Set<object>(),
  bounds: JsonBounds = GENERAL_BOUNDS,
): JsonValue | Invalid {
  if (budget.nodes-- <= 0 || depth > RESEARCH_STATE_LIMITS.jsonDepth) return INVALID
  if (value === null || typeof value === 'boolean') return value
  if (typeof value === 'string') {
    if (bounds.exact) return value.length <= RESEARCH_STATE_LIMITS.promptChars ? value : INVALID
    return value.slice(0, RESEARCH_STATE_LIMITS.textChars)
  }
  if (typeof value === 'number')
    return Number.isFinite(value) ? (Object.is(value, -0) ? 0 : value) : INVALID
  if (typeof value !== 'object' || stack.has(value)) return INVALID
  stack.add(value)
  try {
    if (Array.isArray(value)) {
      if (bounds.exact && value.length > bounds.listItems) return INVALID
      const out: JsonValue[] = []
      for (const row of value.slice(0, bounds.listItems)) {
        const parsed = jsonValue(row, budget, depth + 1, stack, bounds)
        if (parsed !== INVALID) out.push(parsed)
        else if (bounds.exact) return INVALID
      }
      return out
    }
    const keys = Object.keys(value).sort(lexical)
    if (bounds.exact && keys.length > bounds.listItems) return INVALID
    const out: Record<string, JsonValue> = {}
    for (const key of keys.slice(0, bounds.listItems)) {
      // A key is an identity, and an exact read keeps it whole: a board's
      // verification archive is keyed by the canonical JSON of each entry, which
      // runs far past an id's length, and clipping it would collide entries or
      // refuse the board. Bounded by the text cap, as a value is.
      if (bounds.exact && key.length > RESEARCH_STATE_LIMITS.textChars) return INVALID
      // `"__proto__"` is refused rather than skipped, in BOTH modes, and it is
      // the one key that has to be. `out[key] = parsed` runs the legacy
      // prototype setter for it, so the entry vanishes with nothing thrown —
      // and this reader's whole job is to hand back a value the caller can trust
      // is the value it was given. A merge request carrying a claim under that
      // id would come out one claim short, and the hash recomputed over the
      // short copy would agree. Skipping it, which is what a lenient read does
      // with an unparseable VALUE, would be the same silence with a rule in
      // front of it: a value this reader vouches for is different from a map it
      // cannot represent.
      if (!storableId(key)) return INVALID
      const parsed = jsonValue(
        (value as Record<string, unknown>)[key],
        budget,
        depth + 1,
        stack,
        bounds,
      )
      if (parsed === INVALID) {
        if (bounds.exact) return INVALID
        continue
      }
      out[bounds.exact ? key : key.slice(0, RESEARCH_STATE_LIMITS.idChars)] = parsed
    }
    return out
  } finally {
    stack.delete(value)
  }
}

/** A semantic snapshot: whole, or not at all. The board's rule, for the same
 *  reason and one stronger — see `snapshotEntries`. */
const snapshotJson = (value: unknown): JsonValue | Invalid =>
  jsonValue(
    value,
    { nodes: RESEARCH_STATE_LIMITS.snapshotJsonNodes },
    0,
    new Set<object>(),
    SNAPSHOT_BOUNDS,
  )

/** One ledger row's board or delta: whole, or not at all. */
const boardJson = (value: unknown): JsonValue | Invalid =>
  jsonValue(
    value,
    { nodes: RESEARCH_STATE_LIMITS.knowledgeBoardJsonNodes },
    0,
    new Set<object>(),
    BOARD_BOUNDS,
  )

function stable(value: unknown): string {
  try {
    return canonicalSemanticJson(value)
  } catch {
    return ''
  }
}

function uniqueBy<T>(rows: readonly T[], id: (row: T) => string): T[] {
  const sorted = [...rows].sort((a, b) => lexical(id(a), id(b)) || lexical(stable(a), stable(b)))
  const out: T[] = []
  for (const row of sorted) {
    if (out.length === 0 || id(out[out.length - 1]!) !== id(row)) out.push(row)
  }
  return out
}

/** Request order is evidence for prompts, so duplicate removal cannot sort it. */
function uniqueInOrder<T>(rows: readonly T[], id: (row: T) => string): T[] {
  const seen = new Set<string>()
  return rows.filter((row) => {
    const key = id(row)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function model(value: unknown): ModelStamp | null {
  const row = object(value)
  if (!row || !oneOf(row.provider, PROVIDERS)) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const name = requiredText(row.name, RESEARCH_STATE_LIMITS.labelChars)
  return id && name ? { provider: row.provider, id, name } : null
}

function defaultProfile(): EvidenceRuleProfile {
  return normalizeEvidenceProfile(clone(DEFAULT_EVIDENCE_PROFILE))
}

/** A run snapshot cannot acquire a replacement default when its input is absent. */
export function parseFrozenEvidenceProfile(value: unknown): EvidenceRuleProfile | null {
  const row = object(value)
  if (
    !row ||
    !requiredText(row.id, RESEARCH_STATE_LIMITS.idChars) ||
    !requiredText(row.name, RESEARCH_STATE_LIMITS.labelChars) ||
    !['support', 'refutation', 'promotion', 'search', 'budget', 'updates', 'stopping'].every(
      (key) => object(row[key]) !== null,
    )
  )
    return null
  const search = object(row.search)!
  if (search.policy !== undefined && search.policy !== 'recorded-search-v1') return null
  return profile(value, () => {})
}

function profile(value: unknown, diagnostic: DiagnosticSink): EvidenceRuleProfile {
  const base = defaultProfile()
  const row = object(value)
  if (!row) {
    diagnostic({
      code: 'field-defaulted',
      path: 'evidenceProfile',
      message: 'Malformed evidence profile was replaced by the safe default.',
    })
    return base
  }
  const support = object(row.support) ?? {}
  const refutation = object(row.refutation) ?? {}
  const promotion = object(row.promotion) ?? {}
  const search = object(row.search) ?? {}
  const budget = object(row.budget) ?? {}
  const updates = object(row.updates) ?? {}
  const stopping = object(row.stopping) ?? {}
  const rawDesigns = object(row.studyDesigns) ?? {}
  const studyDesigns: EvidenceRuleProfile['studyDesigns'] = {}
  for (const kind of CLAIM_TYPES) {
    const designs = rawDesigns[kind]
    if (!Array.isArray(designs)) continue
    studyDesigns[kind] = [
      ...new Set(designs.filter((item): item is StudyDesign => oneOf(item, STUDY_DESIGNS))),
    ]
  }
  const sourceClasses = Array.isArray(row.admissibleSourceClasses)
    ? [
        ...new Set(
          row.admissibleSourceClasses.filter((item): item is EvidenceSourceClass =>
            oneOf(item, SOURCE_CLASSES),
          ),
        ),
      ]
    : base.admissibleSourceClasses
  const normalized = normalizeEvidenceProfile({
    id: requiredText(row.id, RESEARCH_STATE_LIMITS.idChars) ?? base.id,
    name: requiredText(row.name, RESEARCH_STATE_LIMITS.labelChars) ?? base.name,
    version: integer(row.version, base.version),
    updatedAt: time(row.updatedAt, base.updatedAt),
    admissibleSourceClasses: sourceClasses,
    support: {
      minIndependentUnits: integer(support.minIndependentUnits, base.support.minIndependentUnits),
      minimumReach: oneOf(support.minimumReach, EVIDENCE_REACHES)
        ? support.minimumReach
        : base.support.minimumReach,
    },
    refutation: {
      minIndependentUnits: integer(refutation.minIndependentUnits, CORE_REFUTATION_MIN),
      minIndependentUnitsWithoutVerifiedText: integer(
        refutation.minIndependentUnitsWithoutVerifiedText,
        CORE_REFUTATION_UNCHECKED_MIN,
      ),
      minimumReach: oneOf(refutation.minimumReach, EVIDENCE_REACHES)
        ? refutation.minimumReach
        : base.refutation.minimumReach,
    },
    studyDesigns,
    promotion: {
      requireSourceCheck: bool(promotion.requireSourceCheck, base.promotion.requireSourceCheck),
      requireNoTaint: bool(promotion.requireNoTaint, base.promotion.requireNoTaint),
      requireNoStaleness: bool(promotion.requireNoStaleness, base.promotion.requireNoStaleness),
      maxCheckAgeDays: nullableNonnegative(promotion.maxCheckAgeDays),
      requirePopulationMatch: bool(
        promotion.requirePopulationMatch,
        base.promotion.requirePopulationMatch,
      ),
      requireTimeframeMatch: bool(
        promotion.requireTimeframeMatch,
        base.promotion.requireTimeframeMatch,
      ),
    },
    search: {
      requiredQueryFamilies: strings(search.requiredQueryFamilies),
      requiredRetrievalSources: strings(search.requiredRetrievalSources),
      ...(search.policy === 'recorded-search-v1' ? { policy: 'recorded-search-v1' as const } : {}),
      minimumEstimatedCoverage:
        search.minimumEstimatedCoverage === null
          ? null
          : Math.min(1, nonnegative(search.minimumEstimatedCoverage)),
      allowPartialForSupport: bool(
        search.allowPartialForSupport,
        base.search.allowPartialForSupport,
      ),
      absenceRequiresNoFailures: bool(
        search.absenceRequiresNoFailures,
        base.search.absenceRequiresNoFailures,
      ),
    },
    budget: {
      maxMoves:
        budget.maxMoves === undefined
          ? base.budget.maxMoves
          : budget.maxMoves === null
            ? null
            : integer(budget.maxMoves, 1),
      maxEstimatedUsd: nullableNonnegative(budget.maxEstimatedUsd),
      maxElapsedMinutes: nullableNonnegative(budget.maxElapsedMinutes),
    },
    updates: {
      recheckAfterDays: nullableNonnegative(updates.recheckAfterDays),
      recheckOnCorrection: bool(updates.recheckOnCorrection, base.updates.recheckOnCorrection),
      recheckOnRetraction: bool(updates.recheckOnRetraction, base.updates.recheckOnRetraction),
    },
    stopping: {
      minSupported:
        stopping.minSupported === undefined
          ? base.stopping.minSupported
          : stopping.minSupported === null
            ? null
            : integer(stopping.minSupported, 1),
      minRefuted:
        stopping.minRefuted === undefined
          ? base.stopping.minRefuted
          : stopping.minRefuted === null
            ? null
            : integer(stopping.minRefuted, 1),
      stopOnDecisionCriterion: bool(
        stopping.stopOnDecisionCriterion,
        base.stopping.stopOnDecisionCriterion,
      ),
    },
  })
  return normalized
}

/** Safe defaults retain the core refutation floors and make no completeness claim. */
export function createResearchState(input: CreateResearchStateInput = {}): ResearchState {
  const now = time(input.now, Date.now())
  return {
    kind: RESEARCH_STATE_KIND,
    version: RESEARCH_STATE_VERSION,
    id: requiredText(input.id, RESEARCH_STATE_LIMITS.idChars) ?? 'research',
    createdAt: now,
    updatedAt: now,
    evidenceProfile: input.evidenceProfile
      ? normalizeEvidenceProfile(clone(input.evidenceProfile))
      : defaultProfile(),
    evidenceRecords: [],
    manualEvidenceBindings: [],
    genealogyOverrides: [],
    coverageTraces: [],
    evidenceAnnotations: [],
    claimAnnotations: [],
    decisionCriterionSignals: [],
    claimScopes: [],
    contradictionAnalyses: [],
    falsificationPortfolios: [],
    calibrationTournaments: [],
    semanticCommits: [],
    branchLineages: [],
    semanticWorkspace: null,
    signedCritiques: [],
    knowledgeHistory: { revisions: [], omittedBefore: 0 },
    auditReceipts: [],
  }
}

function evidenceRecord(
  value: unknown,
  diagnostic: DiagnosticSink,
  path: string,
): EvidenceRecord | null {
  const row = object(value)
  if (!row || !oneOf(row.origin, EVIDENCE_ORIGINS)) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const label = requiredText(row.label, RESEARCH_STATE_LIMITS.labelChars)
  if (!id || !label) return null
  const candidate: EvidenceRecord = {
    id,
    origin: row.origin,
    label,
    ...(typeof row.url === 'string' ? { url: row.url.slice(0, 4_000) } : {}),
    ...(typeof row.doi === 'string' ? { doi: row.doi.slice(0, 1_000) } : {}),
    ...(typeof row.text === 'string'
      ? { text: row.text.slice(0, RESEARCH_STATE_LIMITS.textChars) }
      : {}),
    ...(typeof row.textHash === 'string' ? { textHash: row.textHash.slice(0, 64) } : {}),
    ...(typeof row.simHash === 'string' ? { simHash: row.simHash.slice(0, 64) } : {}),
    trialIds: strings(row.trialIds),
    datasetIds: strings(row.datasetIds),
    registryIds: strings(row.registryIds),
    runIds: strings(row.runIds),
    cohortIds: strings(row.cohortIds),
    datasetOverlapGroups: strings(row.datasetOverlapGroups),
    registryOverlapGroups: strings(row.registryOverlapGroups),
    runOverlapGroups: strings(row.runOverlapGroups),
    cohortOverlapGroups: strings(row.cohortOverlapGroups),
    primaryReferences: strings(row.primaryReferences),
    commonOrigins: strings(row.commonOrigins),
  }
  const checked = buildEvidenceGenealogy([candidate])
  const item = checked.items[0]
  if (!item) return null
  for (const issue of checked.diagnostics) {
    diagnostic({
      code: 'field-defaulted',
      path,
      message: issue.message,
    })
  }
  return {
    id: item.id,
    origin: item.origin,
    label: item.label,
    ...(item.url ? { url: item.url } : {}),
    ...(item.doi ? { doi: item.doi } : {}),
    ...(candidate.text !== undefined ? { text: candidate.text } : {}),
    ...(item.textHash ? { textHash: item.textHash } : {}),
    ...(item.simHash ? { simHash: item.simHash } : {}),
    trialIds: item.trialIds,
    datasetIds: item.datasetIds,
    registryIds: item.registryIds,
    runIds: item.runIds,
    cohortIds: item.cohortIds,
    datasetOverlapGroups: item.datasetOverlapGroups,
    registryOverlapGroups: item.registryOverlapGroups,
    runOverlapGroups: item.runOverlapGroups,
    cohortOverlapGroups: item.cohortOverlapGroups,
    primaryReferences: item.primaryReferences,
    commonOrigins: item.commonOrigins,
  }
}

function manualOriginIdentities(record: EvidenceRecord): readonly string[] {
  switch (record.origin) {
    case 'dataset':
      return record.datasetIds ?? []
    case 'trial':
      return record.trialIds ?? []
    case 'cohort':
      return record.cohortIds ?? []
    case 'experimental-run':
    case 'outcome':
      return record.runIds ?? []
    default:
      return []
  }
}

/**
 * Recheck the durable admission facts at every boundary that consumes a manual
 * join. This intentionally does not inspect a URL or DOI: neither proves that a
 * search returned the artefact.
 */
export function manualEvidenceBindingRejectionReasons(
  binding: ManualEvidenceBinding,
  record: EvidenceRecord | undefined,
): string[] {
  const reasons: string[] = []
  if (binding.version !== MANUAL_EVIDENCE_BINDING_VERSION) reasons.push('unsupported-version')
  if (!binding.nodeId.trim()) reasons.push('missing-node-id')
  if (!binding.recordId.trim()) reasons.push('missing-record-id')
  if (binding.id !== manualEvidenceBindingId(binding.nodeId, binding.recordId)) {
    reasons.push('binding-id-mismatch')
  }
  if (!EVIDENCE_STANCES.includes(binding.stance)) reasons.push('invalid-stance')
  if (!MANUAL_CONTENT_SCOPES.includes(binding.contentScope)) reasons.push('invalid-content-scope')
  if (
    !Number.isFinite(binding.createdAt) ||
    binding.createdAt < 0 ||
    !Number.isFinite(binding.updatedAt) ||
    binding.updatedAt < binding.createdAt
  ) {
    reasons.push('invalid-timestamps')
  }
  if (!record || record.id !== binding.recordId) {
    reasons.push('record-not-found')
    return reasons
  }
  if (!MANUAL_BINDABLE_EVIDENCE_ORIGINS.includes(record.origin as ManualBindableEvidenceOrigin)) {
    reasons.push('record-origin-not-manually-bindable')
    return reasons
  }
  if (!manualOriginIdentities(record).some((identity) => identity.trim().length > 0)) {
    reasons.push('record-stable-identity-missing')
  }
  if (binding.contentScope !== 'metadata-only' && !record.text?.trim()) {
    reasons.push('record-text-missing-for-content-scope')
  }
  return reasons
}

function manualEvidenceBinding(
  value: unknown,
  records: ReadonlyMap<string, EvidenceRecord>,
): ManualEvidenceBinding | null {
  const row = object(value)
  if (
    !row ||
    row.version !== MANUAL_EVIDENCE_BINDING_VERSION ||
    !oneOf(row.stance, EVIDENCE_STANCES) ||
    !oneOf(row.contentScope, MANUAL_CONTENT_SCOPES) ||
    typeof row.createdAt !== 'number' ||
    typeof row.updatedAt !== 'number'
  ) {
    return null
  }
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const nodeId = requiredText(row.nodeId, RESEARCH_STATE_LIMITS.idChars)
  const recordId = requiredText(row.recordId, RESEARCH_STATE_LIMITS.idChars)
  if (!id || !nodeId || !recordId) return null
  const candidate: ManualEvidenceBinding = {
    version: MANUAL_EVIDENCE_BINDING_VERSION,
    id,
    nodeId,
    recordId,
    stance: row.stance,
    contentScope: row.contentScope,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  }
  return manualEvidenceBindingRejectionReasons(candidate, records.get(recordId)).length
    ? null
    : candidate
}

function genealogyOverride(value: unknown, itemIds: Set<string>): GenealogyOverride | null {
  const row = object(value)
  if (!row) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const left = requiredText(row.leftId, RESEARCH_STATE_LIMITS.idChars)
  const right = requiredText(row.rightId, RESEARCH_STATE_LIMITS.idChars)
  const reason = requiredText(row.reason, RESEARCH_STATE_LIMITS.textChars)
  if (
    !id ||
    !left ||
    !right ||
    left === right ||
    !itemIds.has(left) ||
    !itemIds.has(right) ||
    (row.decision !== 'same' && row.decision !== 'separate') ||
    typeof row.at !== 'number' ||
    !Number.isFinite(row.at) ||
    row.at < 0 ||
    !reason
  ) {
    return null
  }
  const [leftId, rightId] = lexical(left, right) <= 0 ? [left, right] : [right, left]
  return { id, leftId, rightId, decision: row.decision, reason, at: row.at }
}

function evidenceAnnotation(value: unknown, itemIds: Set<string>): EvidenceAnnotation | null {
  const row = object(value)
  const rawKey = object(row?.key)
  if (
    !row ||
    !rawKey ||
    (rawKey.kind !== 'evidence-record' && rawKey.kind !== 'binding') ||
    typeof row.updatedAt !== 'number' ||
    !Number.isFinite(row.updatedAt) ||
    row.updatedAt < 0
  ) {
    return null
  }
  const id = requiredText(rawKey.id, RESEARCH_STATE_LIMITS.idChars)
  if (!id || (rawKey.kind === 'evidence-record' && !itemIds.has(id))) return null
  if (row.sourceClass !== undefined && !oneOf(row.sourceClass, SOURCE_CLASSES)) {
    return null
  }
  if (row.studyDesign !== undefined && !oneOf(row.studyDesign, STUDY_DESIGNS)) return null
  if (
    row.populationMatch !== undefined &&
    row.populationMatch !== null &&
    typeof row.populationMatch !== 'boolean'
  ) {
    return null
  }
  if (
    row.timeframeMatch !== undefined &&
    row.timeframeMatch !== null &&
    typeof row.timeframeMatch !== 'boolean'
  ) {
    return null
  }
  const stamp = row.model === undefined ? null : model(row.model)
  if (row.model !== undefined && !stamp) return null
  const opId = text(row.opId, RESEARCH_STATE_LIMITS.idChars)
  return {
    key: { kind: rawKey.kind, id },
    ...(row.sourceClass !== undefined ? { sourceClass: row.sourceClass } : {}),
    ...(row.studyDesign !== undefined ? { studyDesign: row.studyDesign } : {}),
    ...(row.populationMatch !== undefined ? { populationMatch: row.populationMatch } : {}),
    ...(row.timeframeMatch !== undefined ? { timeframeMatch: row.timeframeMatch } : {}),
    updatedAt: row.updatedAt,
    ...(opId ? { opId } : {}),
    ...(stamp ? { model: stamp } : {}),
  }
}

const evidenceAnnotationKey = (annotation: EvidenceAnnotation): string =>
  `${annotation.key.kind}:${annotation.key.id}`

function claimAnnotation(value: unknown): ResearchClaimAnnotation | null {
  const row = object(value)
  if (
    !row ||
    !oneOf(row.claimType, CLAIM_TYPES) ||
    typeof row.updatedAt !== 'number' ||
    !Number.isFinite(row.updatedAt) ||
    row.updatedAt < 0
  ) {
    return null
  }
  const nodeId = requiredText(row.nodeId, RESEARCH_STATE_LIMITS.idChars)
  if (!nodeId) return null
  const stamp = row.model === undefined ? null : model(row.model)
  if (row.model !== undefined && !stamp) return null
  const opId = text(row.opId, RESEARCH_STATE_LIMITS.idChars)
  return {
    nodeId,
    claimType: row.claimType,
    updatedAt: row.updatedAt,
    ...(opId ? { opId } : {}),
    ...(stamp ? { model: stamp } : {}),
  }
}

function decisionCriterionSignal(value: unknown): ResearchDecisionCriterionSignal | null {
  const row = object(value)
  if (
    !row ||
    (row.status !== 'met' && row.status !== 'not-met') ||
    typeof row.updatedAt !== 'number' ||
    !Number.isFinite(row.updatedAt) ||
    row.updatedAt < 0
  ) {
    return null
  }
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const criterion = requiredText(row.criterion, RESEARCH_STATE_LIMITS.textChars)
  return id && criterion ? { id, criterion, status: row.status, updatedAt: row.updatedAt } : null
}

function protocol(value: unknown): CoverageProtocol<JsonValue, JsonValue> | null {
  const row = object(value)
  if (!row) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const version = requiredText(row.version, RESEARCH_STATE_LIMITS.idChars)
  if (!id || !version) return null
  const queryFamilies = uniqueBy(
    (Array.isArray(row.queryFamilies) ? row.queryFamilies : [])
      .slice(0, RESEARCH_STATE_LIMITS.listItems)
      .flatMap((raw) => {
        const item = object(raw)
        const familyId = requiredText(item?.id, RESEARCH_STATE_LIMITS.idChars)
        const label = requiredText(item?.label, RESEARCH_STATE_LIMITS.labelChars)
        if (!item || !familyId || !label) return []
        const meta = jsonValue(item.meta)
        return [
          {
            id: familyId,
            label,
            ...(meta !== INVALID ? { meta } : {}),
          },
        ]
      }),
    (item) => item.id,
  )
  const retrievalSources = uniqueBy(
    (Array.isArray(row.retrievalSources) ? row.retrievalSources : [])
      .slice(0, RESEARCH_STATE_LIMITS.listItems)
      .flatMap((raw) => {
        const item = object(raw)
        const sourceId = requiredText(item?.id, RESEARCH_STATE_LIMITS.idChars)
        const label = requiredText(item?.label, RESEARCH_STATE_LIMITS.labelChars)
        if (!item || !sourceId || !label) return []
        const kind = text(item.kind, RESEARCH_STATE_LIMITS.idChars)
        const meta = jsonValue(item.meta)
        return [
          {
            id: sourceId,
            label,
            ...(kind ? { kind } : {}),
            ...(meta !== INVALID ? { meta } : {}),
          },
        ]
      }),
    (item) => item.id,
  )
  let requiredCombinations: RequiredCoverageCombination[] | undefined
  if (row.requiredCombinations !== undefined) {
    if (
      !Array.isArray(row.requiredCombinations) ||
      row.requiredCombinations.length > RESEARCH_STATE_LIMITS.listItems
    ) {
      return null
    }
    const familyIds = new Set(queryFamilies.map((family) => family.id))
    const sourceIds = new Set(retrievalSources.map((source) => source.id))
    const seen = new Set<string>()
    requiredCombinations = []
    for (const raw of row.requiredCombinations) {
      const item = object(raw)
      const queryFamilyId = requiredText(item?.queryFamilyId, RESEARCH_STATE_LIMITS.idChars)
      const retrievalSourceId = requiredText(item?.retrievalSourceId, RESEARCH_STATE_LIMITS.idChars)
      if (
        !queryFamilyId ||
        !retrievalSourceId ||
        !familyIds.has(queryFamilyId) ||
        !sourceIds.has(retrievalSourceId)
      ) {
        return null
      }
      const key = `${queryFamilyId}\u0000${retrievalSourceId}`
      if (seen.has(key)) return null
      seen.add(key)
      requiredCombinations.push({ queryFamilyId, retrievalSourceId })
    }
  }
  return {
    id,
    version,
    queryFamilies,
    retrievalSources,
    ...(requiredCombinations ? { requiredCombinations } : {}),
  }
}

function completion(value: unknown): RoundCompletion | null {
  const row = object(value)
  if (!row) return null
  if (row.kind === 'complete') return { kind: 'complete' }
  if (row.kind !== 'partial' && row.kind !== 'failed') return null
  const code = requiredText(row.code, RESEARCH_STATE_LIMITS.idChars)
  const reason = requiredText(row.reason, RESEARCH_STATE_LIMITS.textChars)
  return code && reason ? { kind: row.kind, code, reason } : null
}

function admission(value: unknown): AdmissionDecision | null {
  const row = object(value)
  if (!row || (row.kind !== 'admitted' && row.kind !== 'rejected')) return null
  const rule = requiredText(row.rule, RESEARCH_STATE_LIMITS.labelChars)
  if (!rule) return null
  if (row.kind === 'admitted') {
    const reason = text(row.reason, RESEARCH_STATE_LIMITS.textChars)
    return { kind: 'admitted', rule, ...(reason ? { reason } : {}) }
  }
  const code = requiredText(row.code, RESEARCH_STATE_LIMITS.idChars)
  const reason = requiredText(row.reason, RESEARCH_STATE_LIMITS.textChars)
  return code && reason ? { kind: 'rejected', rule, code, reason } : null
}

function coverageResult(value: unknown): CoverageResult<JsonValue> | null {
  const row = object(value)
  if (!row || !oneOf(row.reach, RESULT_REACHES)) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const identity = requiredText(row.identity, RESEARCH_STATE_LIMITS.labelChars)
  const admitted = admission(row.admission)
  if (!id || !identity || !admitted) return null
  const meta = jsonValue(row.meta)
  return {
    id,
    identity,
    reach: row.reach,
    admission: admitted,
    ...(meta !== INVALID ? { meta } : {}),
  }
}

function coverageRound(value: unknown): CoverageRoundDraft<JsonValue> | null {
  const row = object(value)
  const query = object(row?.query)
  const boundary = object(row?.boundary)
  if (!row || !query || !boundary) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const queryId = requiredText(query.id, RESEARCH_STATE_LIMITS.idChars)
  const queryText = requiredText(query.text, RESEARCH_STATE_LIMITS.textChars)
  const familyId = requiredText(query.familyId, RESEARCH_STATE_LIMITS.idChars)
  const retrievalSourceId = requiredText(row.retrievalSourceId, RESEARCH_STATE_LIMITS.idChars)
  const searchVersion = requiredText(boundary.searchVersion, RESEARCH_STATE_LIMITS.idChars)
  if (boundary.corpusVersion !== null && typeof boundary.corpusVersion !== 'string') return null
  const corpusVersion =
    boundary.corpusVersion === null
      ? null
      : requiredText(boundary.corpusVersion, RESEARCH_STATE_LIMITS.idChars)
  const complete = completion(row.completion)
  if (
    !id ||
    !queryId ||
    !queryText ||
    !familyId ||
    !retrievalSourceId ||
    !searchVersion ||
    (corpusVersion === null && boundary.corpusVersion !== null) ||
    !complete ||
    typeof boundary.searchedAt !== 'number' ||
    !Number.isFinite(boundary.searchedAt) ||
    boundary.searchedAt < 0
  ) {
    return null
  }
  const results = uniqueBy(
    (Array.isArray(row.results) ? row.results : [])
      .slice(0, RESEARCH_STATE_LIMITS.coverageResultsPerRound)
      .flatMap((result) => {
        const parsed = coverageResult(result)
        return parsed ? [parsed] : []
      }),
    (result) => result.id,
  )
  const reportedResultCount =
    row.reportedResultCount === null
      ? null
      : typeof row.reportedResultCount === 'number' &&
          Number.isInteger(row.reportedResultCount) &&
          row.reportedResultCount >= results.length
        ? row.reportedResultCount
        : undefined
  if (reportedResultCount === undefined) return null
  return {
    id,
    query: { id: queryId, text: queryText, familyId },
    retrievalSourceId,
    boundary: {
      searchedAt: boundary.searchedAt,
      searchVersion,
      corpusVersion,
    },
    completion: complete,
    reportedResultCount,
    results,
  }
}

function coverageTrace(
  value: unknown,
  diagnostic: DiagnosticSink,
  path: string,
): StoredCoverageTrace | null {
  const row = object(value)
  const parsedProtocol = protocol(row?.protocol)
  if (!row || !parsedProtocol) return null
  try {
    let trace = createCoverageTrace<JsonValue, JsonValue, JsonValue>(parsedProtocol)
    const rounds = Array.isArray(row.rounds)
      ? row.rounds.slice(0, RESEARCH_STATE_LIMITS.coverageRoundsPerTrace)
      : []
    if (Array.isArray(row.rounds) && row.rounds.length > rounds.length) {
      diagnostic({
        code: 'limit-applied',
        path: `${path}.rounds`,
        message: `Kept the first ${rounds.length} coverage rounds.`,
      })
    }
    for (let index = 0; index < rounds.length; index++) {
      const draft = coverageRound(rounds[index])
      if (!draft) {
        diagnostic({
          code: 'entry-dropped',
          path: `${path}.rounds[${index}]`,
          message: 'Malformed coverage round was dropped.',
        })
        continue
      }
      try {
        trace = appendCoverageRound(trace, draft)
      } catch (error) {
        diagnostic({
          code: 'entry-dropped',
          path: `${path}.rounds[${index}]`,
          message: error instanceof Error ? error.message : 'Invalid coverage round.',
        })
      }
    }
    return trace
  } catch {
    return null
  }
}

function claimScope(value: unknown): ClaimScope {
  const row = object(value) ?? {}
  const base = emptyClaimScope()
  const quantitative = normalizeQuantitativeFinding(row.quantitative)
  const fieldOrigins = normalizeScopeFieldOrigins(row.fieldOrigins)
  return {
    population: text(row.population, RESEARCH_STATE_LIMITS.labelChars) ?? base.population,
    intervention: text(row.intervention, RESEARCH_STATE_LIMITS.labelChars) ?? base.intervention,
    exposure: text(row.exposure, RESEARCH_STATE_LIMITS.labelChars) ?? base.exposure,
    comparator: text(row.comparator, RESEARCH_STATE_LIMITS.labelChars) ?? base.comparator,
    outcome: text(row.outcome, RESEARCH_STATE_LIMITS.labelChars) ?? base.outcome,
    measurement: text(row.measurement, RESEARCH_STATE_LIMITS.labelChars) ?? base.measurement,
    timeHorizon: text(row.timeHorizon, RESEARCH_STATE_LIMITS.labelChars) ?? base.timeHorizon,
    studyDesign: text(row.studyDesign, RESEARCH_STATE_LIMITS.labelChars) ?? base.studyDesign,
    definition: text(row.definition, RESEARCH_STATE_LIMITS.labelChars) ?? base.definition,
    causalMode: oneOf(row.causalMode, CAUSAL_MODES) ? row.causalMode : base.causalMode,
    direction: oneOf(row.direction, DIRECTIONS) ? row.direction : base.direction,
    ...(row.nullMeaning !== undefined
      ? {
          nullMeaning: oneOf(row.nullMeaning, NULL_MEANINGS)
            ? row.nullMeaning
            : ('unknown' as const),
        }
      : {}),
    ...(quantitative ? { quantitative } : {}),
    ...(fieldOrigins ? { fieldOrigins } : {}),
    assumptions: strings(row.assumptions),
  }
}

function researchClaimScope(value: unknown): ResearchClaimScope | null {
  const row = object(value)
  if (!row) return null
  const nodeId = requiredText(row.nodeId, RESEARCH_STATE_LIMITS.idChars)
  if (!nodeId) return null
  const stamp = row.model === undefined ? null : model(row.model)
  if (row.model !== undefined && !stamp) return null
  const opId = text(row.opId, RESEARCH_STATE_LIMITS.idChars)
  return {
    nodeId,
    scope: claimScope(row.scope),
    updatedAt: time(row.updatedAt),
    ...(opId ? { opId } : {}),
    ...(stamp ? { model: stamp } : {}),
  }
}

function scopedClaim(value: unknown): ScopedClaim | null {
  const row = object(value)
  if (!row) return null
  const nodeId = requiredText(row.nodeId, RESEARCH_STATE_LIMITS.idChars)
  const claim = requiredText(row.claim, RESEARCH_STATE_LIMITS.textChars)
  return nodeId && claim ? { nodeId, claim, scope: claimScope(row.scope) } : null
}

function prediction(value: unknown): TestPrediction | null {
  const row = object(value)
  if (!row || !oneOf(row.bearing, FUTURE_BEARINGS)) return null
  const observation = requiredText(row.observation, RESEARCH_STATE_LIMITS.textChars)
  const implication = requiredText(row.implication, RESEARCH_STATE_LIMITS.textChars)
  const usefulNext = requiredText(row.usefulNext, RESEARCH_STATE_LIMITS.textChars)
  return observation && implication && usefulNext
    ? {
        bearing: row.bearing,
        observation,
        implication,
        usefulNext,
        ...(Object.prototype.hasOwnProperty.call(row, 'compatibleFutureIds')
          ? { compatibleFutureIds: strings(row.compatibleFutureIds) }
          : {}),
      }
    : null
}

function discriminatingTest(value: unknown): DiscriminatingTest | null {
  const row = object(value)
  const cost = object(row?.cost)
  if (!row || !cost || (row.kind !== 'experiment' && row.kind !== 'source-check')) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const title = requiredText(row.title, RESEARCH_STATE_LIMITS.labelChars)
  if (!id || !title) return null
  const source =
    cost.source === 'user' || cost.source === 'estimated' || cost.source === 'unknown'
      ? cost.source
      : 'unknown'
  return {
    id,
    kind: row.kind,
    title,
    // A procedure, in the order it is carried out. What it discriminates is a
    // set of node ids and keeps its canonical order.
    protocol: orderedStrings(row.protocol),
    discriminates: strings(row.discriminates),
    cost: {
      effortHours: nullableNonnegative(cost.effortHours),
      elapsedDays: nullableNonnegative(cost.elapsedDays),
      moneyUsd: nullableNonnegative(cost.moneyUsd),
      source,
    },
    separation: Math.min(1, nonnegative(row.separation)),
    decisionImpact: Math.min(1, nonnegative(row.decisionImpact)),
    reusableValue: Math.min(1, nonnegative(row.reusableValue)),
    predictions: uniqueBy(
      (Array.isArray(row.predictions) ? row.predictions : []).slice(0, 3).flatMap((raw) => {
        const parsed = prediction(raw)
        return parsed ? [parsed] : []
      }),
      (item) => item.bearing,
    ),
  }
}

function contradictionAnalysis(value: unknown): ContradictionAnalysis | null {
  const row = object(value)
  if (!row) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const left = scopedClaim(row.left)
  const right = scopedClaim(row.right)
  if (!id || !left || !right) return null
  const stamp = row.model === undefined ? null : model(row.model)
  if (row.model !== undefined && !stamp) return null
  const tests = uniqueBy(
    (Array.isArray(row.tests) ? row.tests : [])
      .slice(0, RESEARCH_STATE_LIMITS.listItems)
      .flatMap((raw) => {
        const parsed = discriminatingTest(raw)
        return parsed ? [parsed] : []
      }),
    (test) => test.id,
  )
  const opId = text(row.opId, RESEARCH_STATE_LIMITS.idChars)
  const original = object(row.originalInterpretation)
  const originalMethod = text(original?.method, RESEARCH_STATE_LIMITS.idChars)
  const method = text(row.method, RESEARCH_STATE_LIMITS.idChars) ?? LEGACY_CONTRADICTION_METHOD
  const originalInterpretation =
    original && originalMethod && oneOf(original.verdict, CONTRADICTION_VERDICTS)
      ? { method: originalMethod, verdict: original.verdict, rules: strings(original.rules) }
      : method !== CONTRADICTION_METHOD && oneOf(row.verdict, CONTRADICTION_VERDICTS)
        ? { method, verdict: row.verdict, rules: strings(row.rules) }
        : undefined
  return completeContradictionAnalysis({
    id,
    left,
    right,
    tests,
    createdAt: time(row.createdAt),
    ...(originalInterpretation ? { originalInterpretation } : {}),
    ...(opId ? { opId } : {}),
    ...(stamp ? { model: stamp } : {}),
  })
}

/**
 * `noCriteria` is the portfolio's own empty criterion catalog. There a future
 * has no decision to change and its narrative is empty by instruction, which the
 * engine admits — so this codec must too, or a portfolio accepted live would
 * lose all three futures on reload. A legacy row, with no catalog, keeps the old
 * requirement.
 */
function falsificationFuture(value: unknown, noCriteria: boolean): FalsificationFuture | null {
  const row = object(value)
  if (!row || !oneOf(row.bearing, FUTURE_BEARINGS)) return null
  const title = requiredText(row.title, RESEARCH_STATE_LIMITS.labelChars)
  const decisionEffect = noCriteria
    ? text(row.decisionEffect, RESEARCH_STATE_LIMITS.textChars)
    : requiredText(row.decisionEffect, RESEARCH_STATE_LIMITS.textChars)
  if (!title || decisionEffect === null) return null
  const claims = (Array.isArray(row.claims) ? row.claims : [])
    .slice(0, RESEARCH_STATE_LIMITS.listItems)
    .flatMap((raw) => {
      const claim = object(raw)
      const label = requiredText(claim?.label, RESEARCH_STATE_LIMITS.labelChars)
      const content = requiredText(claim?.claim, RESEARCH_STATE_LIMITS.textChars)
      const reasoning = requiredText(claim?.reasoning, RESEARCH_STATE_LIMITS.textChars)
      return label && content && reasoning ? [{ label, claim: content, reasoning }] : []
    })
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const decisionEffects: FalsificationDecisionEffect[] | undefined =
    row.decisionEffects !== undefined
      ? uniqueBy(
          (Array.isArray(row.decisionEffects) ? row.decisionEffects : [])
            .slice(0, RESEARCH_STATE_LIMITS.listItems)
            .flatMap((raw) => {
              const effect = object(raw)
              const criterionId = requiredText(effect?.criterionId, RESEARCH_STATE_LIMITS.idChars)
              if (
                !criterionId ||
                (effect?.disposition !== 'advance' &&
                  effect?.disposition !== 'reject' &&
                  effect?.disposition !== 'defer' &&
                  effect?.disposition !== 'unchanged' &&
                  effect?.disposition !== 'unknown')
              ) {
                return []
              }
              return [{ criterionId, disposition: effect.disposition }]
            }),
          (effect) => effect.criterionId,
        )
      : undefined
  return {
    ...(id ? { id } : {}),
    bearing: row.bearing,
    title,
    claims,
    decisionEffect,
    usefulWork: strings(row.usefulWork),
    ...(decisionEffects !== undefined ? { decisionEffects } : {}),
    ...(row.usefulWorkIds !== undefined ? { usefulWorkIds: strings(row.usefulWorkIds) } : {}),
  }
}

function falsificationPortfolio(value: unknown): FalsificationPortfolio | null {
  const row = object(value)
  const structural = object(row?.structuralValue)
  if (!row || !structural) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const targetId = requiredText(row.targetId, RESEARCH_STATE_LIMITS.idChars)
  const targetClaim = requiredText(row.targetClaim, RESEARCH_STATE_LIMITS.textChars)
  if (!id || !targetId || !targetClaim) return null
  const stamp = row.model === undefined ? null : model(row.model)
  if (row.model !== undefined && !stamp) return null
  const tests = uniqueBy(
    (Array.isArray(row.tests) ? row.tests : [])
      .slice(0, RESEARCH_STATE_LIMITS.listItems)
      .flatMap((raw) => {
        const parsed = discriminatingTest(raw)
        return parsed ? [parsed] : []
      }),
    (test) => test.id,
  )
  const criteria =
    row.criteria !== undefined
      ? uniqueBy(
          (Array.isArray(row.criteria) ? row.criteria : [])
            .slice(0, RESEARCH_STATE_LIMITS.listItems)
            .flatMap((raw) => {
              const criterion = object(raw)
              const criterionId = requiredText(criterion?.id, RESEARCH_STATE_LIMITS.idChars)
              const criterionText = requiredText(criterion?.text, RESEARCH_STATE_LIMITS.textChars)
              return criterionId && criterionText ? [{ id: criterionId, text: criterionText }] : []
            }),
          (criterion) => criterion.id,
        )
      : undefined
  const futures = uniqueBy(
    (Array.isArray(row.futures) ? row.futures : []).slice(0, 3).flatMap((raw) => {
      const parsed = falsificationFuture(raw, criteria?.length === 0)
      return parsed ? [parsed] : []
    }),
    (future) => future.bearing,
  )
  const workItems =
    row.workItems !== undefined
      ? uniqueBy(
          (Array.isArray(row.workItems) ? row.workItems : [])
            .slice(0, RESEARCH_STATE_LIMITS.listItems)
            .flatMap((raw) => {
              const work = object(raw)
              const workId = requiredText(work?.id, RESEARCH_STATE_LIMITS.idChars)
              const label = requiredText(work?.label, RESEARCH_STATE_LIMITS.labelChars)
              return workId && label ? [{ id: workId, label }] : []
            }),
          (work) => work.id,
        )
      : undefined
  const opId = text(row.opId, RESEARCH_STATE_LIMITS.idChars)
  try {
    let complete = completeFalsificationPortfolio({
      id,
      targetId,
      targetClaim,
      createdAt: time(row.createdAt),
      ...(opId ? { opId } : {}),
      ...(stamp ? { model: stamp } : {}),
      tests,
      futures,
      ...(criteria !== undefined ? { criteria } : {}),
      ...(workItems !== undefined ? { workItems } : {}),
      structuralValue: {
        downstreamClaims: integer(structural.downstreamClaims),
        decisionCriteria: integer(structural.decisionCriteria),
        score: nonnegative(structural.score),
      },
    })
    const resolved = object(row.resolved)
    if (resolved && oneOf(resolved.bearing, FUTURE_BEARINGS)) {
      const bearing =
        resolved.bearing === 'positive'
          ? 'supported'
          : resolved.bearing === 'negative'
            ? 'refuted'
            : 'inconclusive'
      complete = resolveFalsificationPortfolio(complete, bearing, time(resolved.outcomeRecordedAt))
    }
    return complete
  } catch {
    return null
  }
}

function tournamentSubmission(value: unknown): TournamentSubmission | null {
  const row = object(value)
  if (!row || !oneOf(row.stage, TOURNAMENT_STAGES)) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const stamp = model(row.model)
  const opId = requiredText(row.opId, RESEARCH_STATE_LIMITS.idChars)
  if (
    !id ||
    !stamp ||
    !opId ||
    typeof row.round !== 'number' ||
    !Number.isInteger(row.round) ||
    row.round < 1 ||
    (row.position !== 'holds' &&
      row.position !== 'holds-weaker' &&
      row.position !== 'fails' &&
      row.position !== 'undecidable')
  ) {
    return null
  }
  return {
    id,
    round: row.round,
    stage: row.stage,
    model: stamp,
    blindToSubmissionIds: strings(row.blindToSubmissionIds),
    position: row.position,
    ...(typeof row.supportProbability === 'number' &&
    Number.isFinite(row.supportProbability) &&
    row.supportProbability >= 0 &&
    row.supportProbability <= 1
      ? { supportProbability: row.supportProbability }
      : {}),
    // The argument as it was made, and as the other entrant was shown it: the
    // next stage reads these in this order, so the record keeps it.
    claims: orderedStrings(row.claims),
    objections: orderedStrings(row.objections),
    predictions: orderedStrings(row.predictions),
    errorTags: [
      ...new Set(
        (Array.isArray(row.errorTags) ? row.errorTags : []).filter(
          (kind): kind is ReasoningErrorKind => oneOf(kind, ERROR_KINDS),
        ),
      ),
    ].sort(lexical),
    authoredAt: time(row.authoredAt),
    opId,
  }
}

function tournamentRound(value: unknown): TournamentRound | null {
  const row = object(value)
  if (
    !row ||
    !oneOf(row.stage, TOURNAMENT_STAGES) ||
    typeof row.number !== 'number' ||
    !Number.isInteger(row.number) ||
    row.number < 1
  ) {
    return null
  }
  const submissions = uniqueBy(
    (Array.isArray(row.submissions) ? row.submissions : [])
      .slice(0, RESEARCH_STATE_LIMITS.listItems)
      .flatMap((raw) => {
        const parsed = tournamentSubmission(raw)
        return parsed && parsed.round === row.number && parsed.stage === row.stage ? [parsed] : []
      }),
    (submission) => submission.id,
  )
  return { number: row.number, stage: row.stage, submissions }
}

function calibrationTournament(value: unknown): CalibrationTournament | null {
  const row = object(value)
  if (!row) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const sessionId = requiredText(row.sessionId, RESEARCH_STATE_LIMITS.idChars)
  const domain = requiredText(row.domain, RESEARCH_STATE_LIMITS.labelChars)
  const targetId = requiredText(row.targetId, RESEARCH_STATE_LIMITS.idChars)
  const targetClaim = requiredText(row.targetClaim, RESEARCH_STATE_LIMITS.textChars)
  if (!id || !sessionId || !domain || !targetId || !targetClaim) return null
  const entrants = uniqueBy(
    (Array.isArray(row.entrants) ? row.entrants : [])
      .slice(0, RESEARCH_STATE_LIMITS.listItems)
      .flatMap((raw) => {
        const parsed = model(raw)
        return parsed ? [parsed] : []
      }),
    (stamp) => `${stamp.provider}\0${stamp.id}`,
  )
  const rounds = uniqueBy(
    (Array.isArray(row.rounds) ? row.rounds : [])
      .slice(0, RESEARCH_STATE_LIMITS.listItems)
      .flatMap((raw) => {
        const parsed = tournamentRound(raw)
        return parsed ? [parsed] : []
      }),
    (round) => String(round.number).padStart(12, '0'),
  )
  const startedAt = time(row.startedAt)
  const completedAt =
    typeof row.completedAt === 'number' &&
    Number.isFinite(row.completedAt) &&
    row.completedAt >= startedAt
      ? row.completedAt
      : undefined
  return {
    id,
    sessionId,
    domain,
    targetId,
    targetClaim,
    ...(oneOf(row.targetMove, NODE_ORIGINS) ? { targetMove: row.targetMove } : {}),
    entrants,
    rounds,
    startedAt,
    ...(completedAt !== undefined ? { completedAt } : {}),
  }
}

function sourceSnapshot(value: unknown): SourceSnapshotReceipt | null {
  const row = object(value)
  if (!row) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const suppliedUrl = row.url
  const url =
    typeof suppliedUrl === 'string' && isHttpUrl(suppliedUrl) ? canonicalUrl(suppliedUrl) : null
  if (suppliedUrl !== undefined && suppliedUrl !== null && !url) return null
  const identity = requiredText(row.identity, RESEARCH_STATE_LIMITS.textChars) ?? url
  const locator = requiredText(row.locator, RESEARCH_STATE_LIMITS.textChars) ?? url
  const sha256 =
    typeof row.sha256 === 'string' && HASH.test(row.sha256) ? row.sha256.toLowerCase() : null
  if (!id || !identity || !locator || !sha256) return null
  return {
    id,
    identity,
    locator,
    url,
    capturedAt: time(row.capturedAt),
    sha256,
    bytes: nullableInteger(row.bytes),
  }
}

function rawSearchResult(
  value: unknown,
  snapshotHashes: Set<string>,
): RawSearchResultReceipt | null {
  const row = object(value)
  if (!row) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const queryId = requiredText(row.queryId, RESEARCH_STATE_LIMITS.idChars)
  const retrievalSourceId = requiredText(row.retrievalSourceId, RESEARCH_STATE_LIMITS.idChars)
  const suppliedUrl = row.url
  const url =
    typeof suppliedUrl === 'string' && isHttpUrl(suppliedUrl) ? canonicalUrl(suppliedUrl) : null
  if (suppliedUrl !== undefined && suppliedUrl !== null && !url) return null
  const identity = requiredText(row.identity, RESEARCH_STATE_LIMITS.textChars) ?? url
  const locator = requiredText(row.locator, RESEARCH_STATE_LIMITS.textChars) ?? url
  const title = requiredText(row.title, RESEARCH_STATE_LIMITS.labelChars)
  if (!id || !queryId || !retrievalSourceId || !identity || !locator || !title) return null
  const sourceSnapshotHash =
    typeof row.sourceSnapshotHash === 'string' &&
    snapshotHashes.has(row.sourceSnapshotHash.toLowerCase())
      ? row.sourceSnapshotHash.toLowerCase()
      : undefined
  const raw = jsonValue(row.raw)
  const snippet = text(row.snippet, RESEARCH_STATE_LIMITS.textChars)
  return {
    id,
    queryId,
    retrievalSourceId,
    rank: nullableInteger(row.rank),
    identity,
    locator,
    url,
    title,
    ...(snippet ? { snippet } : {}),
    returnedAt: time(row.returnedAt),
    ...(sourceSnapshotHash ? { sourceSnapshotHash } : {}),
    ...(raw !== INVALID ? { raw } : {}),
  }
}

function citation(
  value: unknown,
  rawResults: Map<string, RawSearchResultReceipt>,
  snapshotHashes: Set<string>,
  diagnostic: DiagnosticSink,
  path: string,
): CitationAuditReceipt | null {
  const row = object(value)
  if (!row || (row.decision !== 'admitted' && row.decision !== 'rejected')) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const url = typeof row.url === 'string' && isHttpUrl(row.url) ? canonicalUrl(row.url) : null
  const title = requiredText(row.title, RESEARCH_STATE_LIMITS.labelChars)
  const rule = requiredText(row.rule, RESEARCH_STATE_LIMITS.labelChars)
  const reason = requiredText(row.reason, RESEARCH_STATE_LIMITS.textChars)
  if (!id || !url || !title || !rule || !reason) return null
  const requestedRawId = text(row.rawResultId, RESEARCH_STATE_LIMITS.idChars)
  const returned = requestedRawId ? rawResults.get(requestedRawId) : undefined
  const wasReturned = returned !== undefined && returned.url !== null && returned.url === url
  let decision: CitationAuditReceipt['decision'] = row.decision
  let admittedRule = rule
  let admittedReason = reason
  let rawResultId: string | null = requestedRawId && returned ? requestedRawId : null
  if (decision === 'admitted' && !wasReturned) {
    decision = 'rejected'
    admittedRule = 'research.citation-returned.v1'
    admittedReason =
      'Rejected during import because the citation URL was not in the raw search results.'
    rawResultId = null
    diagnostic({
      code: 'citation-gate',
      path,
      message: 'An admitted citation not returned by search was converted to rejected.',
    })
  }
  const evidenceRecordId = text(row.evidenceRecordId, RESEARCH_STATE_LIMITS.idChars)
  const sourceSnapshotHash =
    typeof row.sourceSnapshotHash === 'string' &&
    snapshotHashes.has(row.sourceSnapshotHash.toLowerCase())
      ? row.sourceSnapshotHash.toLowerCase()
      : undefined
  return {
    id,
    rawResultId,
    url,
    title,
    decision,
    rule: admittedRule,
    reason: admittedReason,
    ...(evidenceRecordId ? { evidenceRecordId } : {}),
    ...(sourceSnapshotHash ? { sourceSnapshotHash } : {}),
  }
}

function auditReceipt(
  value: unknown,
  diagnostic: DiagnosticSink,
  path: string,
): ResearchAuditReceipt | null {
  const row = object(value)
  const usage = object(row?.usage)
  if (
    !row ||
    !usage ||
    row.version !== RESEARCH_AUDIT_RECEIPT_VERSION ||
    !oneOf(row.kind, AUDIT_KINDS)
  ) {
    return null
  }
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const opId = requiredText(row.opId, RESEARCH_STATE_LIMITS.idChars)
  const stamp = model(row.model)
  if (!id || !opId || !stamp) return null
  const startedAt = time(row.startedAt)
  if (
    typeof usage.inputTokens !== 'number' ||
    !Number.isFinite(usage.inputTokens) ||
    usage.inputTokens < 0 ||
    typeof usage.outputTokens !== 'number' ||
    !Number.isFinite(usage.outputTokens) ||
    usage.outputTokens < 0 ||
    typeof usage.searchRequests !== 'number' ||
    !Number.isFinite(usage.searchRequests) ||
    usage.searchRequests < 0 ||
    typeof row.completedAt !== 'number' ||
    !Number.isFinite(row.completedAt) ||
    row.completedAt < startedAt
  ) {
    return null
  }
  const prompts = uniqueInOrder(
    boundedRows(
      row.prompts,
      RESEARCH_STATE_LIMITS.promptsPerReceipt,
      `${path}.prompts`,
      diagnostic,
    ).flatMap((raw) => {
      const prompt = object(raw)
      const promptId = requiredText(prompt?.id, RESEARCH_STATE_LIMITS.idChars)
      const content = text(prompt?.content, RESEARCH_STATE_LIMITS.promptChars)
      return prompt && promptId && content !== null && oneOf(prompt.role, PROMPT_ROLES)
        ? [{ id: promptId, role: prompt.role, content }]
        : []
    }),
    (prompt) => prompt.id,
  )
  const sourceSnapshots = uniqueBy(
    boundedRows(
      row.sourceSnapshots,
      RESEARCH_STATE_LIMITS.snapshotsPerReceipt,
      `${path}.sourceSnapshots`,
      diagnostic,
    ).flatMap((raw) => {
      const parsed = sourceSnapshot(raw)
      return parsed ? [parsed] : []
    }),
    (snapshot) => snapshot.id,
  )
  const snapshotHashes = new Set(sourceSnapshots.map((snapshot) => snapshot.sha256))
  const rawSearchResults = uniqueBy(
    boundedRows(
      row.rawSearchResults,
      RESEARCH_STATE_LIMITS.rawResultsPerReceipt,
      `${path}.rawSearchResults`,
      diagnostic,
    ).flatMap((raw) => {
      const parsed = rawSearchResult(raw, snapshotHashes)
      return parsed ? [parsed] : []
    }),
    (result) => result.id,
  )
  const rawById = new Map(rawSearchResults.map((result) => [result.id, result]))
  const citations = uniqueBy(
    boundedRows(
      row.citations,
      RESEARCH_STATE_LIMITS.citationsPerReceipt,
      `${path}.citations`,
      diagnostic,
    ).flatMap((raw, index) => {
      const parsed = citation(
        raw,
        rawById,
        snapshotHashes,
        diagnostic,
        `${path}.citations[${index}]`,
      )
      return parsed ? [parsed] : []
    }),
    (item) => item.id,
  )
  const rawOutcome = object(row.outcome)
  const outcome: ResearchAuditOutcome | undefined =
    rawOutcome?.kind === 'completed'
      ? { kind: 'completed' }
      : rawOutcome && oneOf(rawOutcome.kind, AUDIT_OUTCOME_KINDS)
        ? (() => {
            const code = requiredText(rawOutcome.code, RESEARCH_STATE_LIMITS.labelChars)
            const reason = requiredText(rawOutcome.reason, RESEARCH_STATE_LIMITS.textChars)
            return code && reason ? { kind: rawOutcome.kind, code, reason } : undefined
          })()
        : undefined
  return {
    version: RESEARCH_AUDIT_RECEIPT_VERSION,
    id,
    opId,
    kind: row.kind,
    model: stamp,
    prompts,
    startedAt,
    completedAt: row.completedAt,
    usage: {
      inputTokens: integer(usage.inputTokens),
      outputTokens: integer(usage.outputTokens),
      reasoningTokens: nullableInteger(usage.reasoningTokens),
      cacheReadInputTokens: nullableInteger(usage.cacheReadInputTokens),
      cacheWriteInputTokens: nullableInteger(usage.cacheWriteInputTokens),
      searchRequests: integer(usage.searchRequests),
      estimatedUsd: nullableNonnegative(usage.estimatedUsd),
    },
    ...(outcome ? { outcome } : {}),
    rawSearchResults,
    citations,
    sourceSnapshots,
  }
}

function semanticActor(value: unknown): SemanticActor | null {
  const row = object(value)
  if (!row) return null
  if (row.kind === 'person' || row.kind === 'system') {
    const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
    const name = text(row.name, RESEARCH_STATE_LIMITS.labelChars)
    return id ? { kind: row.kind, id, ...(name ? { name } : {}) } : null
  }
  if (row.kind === 'model') {
    const stamp = model(row.model)
    const opId = text(row.opId, RESEARCH_STATE_LIMITS.idChars)
    return stamp ? { kind: 'model', model: stamp, ...(opId ? { opId } : {}) } : null
  }
  if (row.kind === 'source-check') {
    const nodeId = requiredText(row.nodeId, RESEARCH_STATE_LIMITS.idChars)
    const stamp = model(row.model)
    const verificationOpId = text(row.verificationOpId, RESEARCH_STATE_LIMITS.idChars)
    return nodeId && stamp
      ? {
          kind: 'source-check',
          nodeId,
          model: stamp,
          ...(verificationOpId ? { verificationOpId } : {}),
        }
      : null
  }
  if (row.kind === 'outcome') {
    const nodeId = requiredText(row.nodeId, RESEARCH_STATE_LIMITS.idChars)
    const source = text(row.source, RESEARCH_STATE_LIMITS.labelChars)
    return nodeId
      ? { kind: 'outcome', nodeId, recordedAt: time(row.recordedAt), ...(source ? { source } : {}) }
      : null
  }
  return null
}

function branchLineage(value: unknown): BranchLineage | null {
  const row = object(value)
  if (!row || row.version !== BRANCH_LINEAGE_VERSION) return null
  const branchId = requiredText(row.branchId, RESEARCH_STATE_LIMITS.idChars)
  const createdBy = semanticActor(row.createdBy)
  if (!branchId || !createdBy) return null
  const parentBranchId =
    row.parentBranchId === null
      ? null
      : requiredText(row.parentBranchId, RESEARCH_STATE_LIMITS.idChars)
  const forkedFromCommit =
    row.forkedFromCommit === null
      ? null
      : typeof row.forkedFromCommit === 'string' && HASH.test(row.forkedFromCommit)
        ? row.forkedFromCommit.toLowerCase()
        : undefined
  if (parentBranchId === undefined || forkedFromCommit === undefined) return null
  const depth = integer(row.depth)
  if (
    (parentBranchId === null && (forkedFromCommit !== null || depth !== 0)) ||
    (parentBranchId !== null && (!forkedFromCommit || depth < 1 || parentBranchId === branchId))
  ) {
    return null
  }
  return {
    version: BRANCH_LINEAGE_VERSION,
    branchId,
    parentBranchId,
    forkedFromCommit,
    depth,
    createdAt: time(row.createdAt),
    createdBy,
  }
}

function semanticWorkspace(value: unknown): SemanticWorkspace | null {
  const row = object(value)
  if (!row || row.version !== SEMANTIC_WORKSPACE_VERSION) return null
  const checkedOutBranchId = requiredText(row.checkedOutBranchId, RESEARCH_STATE_LIMITS.idChars)
  const checkedOutCommitHash =
    typeof row.checkedOutCommitHash === 'string' && HASH.test(row.checkedOutCommitHash)
      ? row.checkedOutCommitHash.toLowerCase()
      : null
  return checkedOutBranchId && checkedOutCommitHash
    ? {
        version: SEMANTIC_WORKSPACE_VERSION,
        checkedOutBranchId,
        checkedOutCommitHash,
      }
    : null
}

function validSemanticProfile(value: unknown): boolean {
  const semanticProfile = object(value)
  if (
    !semanticProfile ||
    typeof semanticProfile.seed !== 'string' ||
    (semanticProfile.frame !== 'claim' &&
      semanticProfile.frame !== 'question' &&
      semanticProfile.frame !== 'goal') ||
    !Array.isArray(semanticProfile.brief) ||
    (semanticProfile.project !== null && typeof semanticProfile.project !== 'string')
  ) {
    return false
  }
  for (const directive of semanticProfile.brief) {
    const row = object(directive)
    if (
      !row ||
      (row.kind !== 'constraint' &&
        row.kind !== 'given' &&
        row.kind !== 'exclude' &&
        row.kind !== 'criterion') ||
      typeof row.text !== 'string'
    ) {
      return false
    }
  }
  return true
}

function validSemanticNode(id: string, value: unknown): boolean {
  const node = object(value)
  const claim = object(node?.claim)
  const evidence = object(node?.evidence)
  const conclusion = object(node?.conclusion)
  return !(
    !node ||
    !claim ||
    !evidence ||
    !conclusion ||
    claim.id !== id ||
    typeof claim.claim !== 'string' ||
    typeof claim.label !== 'string' ||
    !Array.isArray(evidence.evidence) ||
    typeof conclusion.confidence !== 'number' ||
    !Number.isFinite(conclusion.confidence) ||
    typeof conclusion.novelty !== 'number' ||
    !Number.isFinite(conclusion.novelty) ||
    typeof conclusion.tension !== 'number' ||
    !Number.isFinite(conclusion.tension) ||
    !Array.isArray(conclusion.nextSteps) ||
    typeof conclusion.terminal !== 'boolean'
  )
}

function validSemanticSnapshot(value: JsonValue): value is JsonValue & SemanticSnapshot {
  const snapshot = object(value)
  const nodes = object(snapshot?.nodes)
  const bridges = object(snapshot?.bridges)
  const archive = object(snapshot?.verificationArchive)
  if (
    !snapshot ||
    snapshot.version !== SEMANTIC_SNAPSHOT_VERSION ||
    !validSemanticProfile(snapshot.profile) ||
    !nodes ||
    !bridges ||
    !archive
  ) {
    return false
  }
  for (const [id, valueNode] of Object.entries(nodes)) {
    if (!validSemanticNode(id, valueNode)) return false
  }
  return true
}

/**
 * A delta is held to the shape a whole board is, section by section: a node that
 * changed is a whole valid node, and an entry that left is null. The `research`
 * section may be null, which is a value — a board with no research state — and
 * is told from an unchanged section by the key being present at all.
 */
function validKnowledgeDelta(value: JsonValue): value is JsonValue & KnowledgeDelta {
  const delta = object(value)
  if (!delta || !requiredText(delta.base, RESEARCH_STATE_LIMITS.idChars)) return false
  if (delta.profile !== undefined && !validSemanticProfile(delta.profile)) return false
  if (delta.research !== undefined && delta.research !== null && !object(delta.research)) {
    return false
  }
  if (delta.nodes !== undefined) {
    const nodes = object(delta.nodes)
    if (!nodes) return false
    for (const [id, node] of Object.entries(nodes)) {
      if (node !== null && !validSemanticNode(id, node)) return false
    }
  }
  for (const section of ['bridges', 'verificationArchive'] as const) {
    if (delta[section] === undefined) continue
    const entries = object(delta[section])
    if (!entries) return false
    for (const entry of Object.values(entries)) {
      if (entry !== null && !object(entry)) return false
    }
  }
  return true
}

function semanticCommit(value: unknown): SemanticCommit | null {
  const row = object(value)
  if (!row || row.version !== SEMANTIC_COMMIT_VERSION) return null
  const hash = typeof row.hash === 'string' && HASH.test(row.hash) ? row.hash.toLowerCase() : null
  const parentHash =
    row.parentHash === null
      ? null
      : typeof row.parentHash === 'string' && HASH.test(row.parentHash)
        ? row.parentHash.toLowerCase()
        : undefined
  const branchId = requiredText(row.branchId, RESEARCH_STATE_LIMITS.idChars)
  const actor = semanticActor(row.actor)
  const message = text(row.message, RESEARCH_STATE_LIMITS.textChars)
  const snapshotHash =
    typeof row.snapshotHash === 'string' && HASH.test(row.snapshotHash)
      ? row.snapshotHash.toLowerCase()
      : null
  const snapshotValue = jsonValue(row.snapshot)
  if (
    !hash ||
    parentHash === undefined ||
    !branchId ||
    !actor ||
    message === null ||
    !snapshotHash ||
    snapshotValue === INVALID ||
    !validSemanticSnapshot(snapshotValue) ||
    typeof row.sequence !== 'number' ||
    !Number.isInteger(row.sequence) ||
    row.sequence < 0 ||
    typeof row.createdAt !== 'number' ||
    !Number.isFinite(row.createdAt) ||
    row.createdAt < 0
  ) {
    return null
  }
  return {
    version: SEMANTIC_COMMIT_VERSION,
    hash,
    parentHash,
    branchId,
    sequence: row.sequence,
    createdAt: row.createdAt,
    actor,
    message,
    snapshotHash,
    snapshot: snapshotValue,
  }
}

/**
 * One ledger row: a whole board, or the delta after the row before it — exactly
 * one of the two, and each parsed whole or refused (`BOARD_BOUNDS`). Whether the
 * delta's base is in fact the row before it is the chain's question, asked once
 * the rows are in order, in `resolveKnowledgeRows`.
 */
function knowledgeRevision(value: unknown): KnowledgeRevision | null {
  const row = object(value)
  if (!row || row.version !== KNOWLEDGE_REVISION_VERSION) return null
  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars)
  const reason = requiredText(row.reason, RESEARCH_STATE_LIMITS.labelChars)
  const actor = semanticActor(row.actor)
  const eventId = text(row.eventId, RESEARCH_STATE_LIMITS.idChars)
  if (
    !id ||
    !reason ||
    !actor ||
    typeof row.recordedAt !== 'number' ||
    !Number.isFinite(row.recordedAt) ||
    row.recordedAt < 0 ||
    (row.snapshot === undefined) === (row.delta === undefined)
  ) {
    return null
  }
  const held = {
    version: KNOWLEDGE_REVISION_VERSION,
    id,
    recordedAt: row.recordedAt,
    reason,
    actor,
    ...(eventId ? { eventId } : {}),
  }
  if (row.snapshot !== undefined) {
    const snapshot = boardJson(row.snapshot)
    if (snapshot === INVALID || !validSemanticSnapshot(snapshot)) return null
    return { ...held, snapshot }
  }
  const delta = boardJson(row.delta)
  if (delta === INVALID || !validKnowledgeDelta(delta)) return null
  return { ...held, delta }
}

function personActor(value: unknown): PersonActor | null {
  const actor = semanticActor(value)
  return actor?.kind === 'person' ? actor : null
}

function critiqueFinding(value: unknown): CritiqueFinding | null {
  const row = object(value)
  if (
    !row ||
    (row.severity !== 'note' && row.severity !== 'concern' && row.severity !== 'blocking')
  ) {
    return null
  }
  const path = requiredText(row.path, RESEARCH_STATE_LIMITS.labelChars)
  const note = requiredText(row.note, RESEARCH_STATE_LIMITS.textChars)
  return path && note ? { path, severity: row.severity, note } : null
}

function critiquePayload(value: unknown): CritiquePayload | null {
  const row = object(value)
  if (!row || row.version !== CRITIQUE_VERSION) return null
  const critiqueId = requiredText(row.critiqueId, RESEARCH_STATE_LIMITS.idChars)
  const branch = branchLineage(row.branch)
  const reviewer = personActor(row.reviewer)
  const baseCommitHash =
    typeof row.baseCommitHash === 'string' && HASH.test(row.baseCommitHash)
      ? row.baseCommitHash.toLowerCase()
      : null
  const headCommitHash =
    typeof row.headCommitHash === 'string' && HASH.test(row.headCommitHash)
      ? row.headCommitHash.toLowerCase()
      : null
  const body = text(row.body, RESEARCH_STATE_LIMITS.textChars)
  if (!critiqueId || !branch || !reviewer || !baseCommitHash || !headCommitHash || body === null) {
    return null
  }
  const findings = (Array.isArray(row.findings) ? row.findings : [])
    .slice(0, RESEARCH_STATE_LIMITS.listItems)
    .flatMap((raw) => {
      const parsed = critiqueFinding(raw)
      return parsed ? [parsed] : []
    })
  return {
    version: CRITIQUE_VERSION,
    critiqueId,
    branch,
    baseCommitHash,
    headCommitHash,
    reviewer,
    body,
    findings,
    createdAt: time(row.createdAt),
  }
}

function signedCritique(value: unknown): SignedCritiqueEnvelope | null {
  const row = object(value)
  if (
    !row ||
    row.version !== CRITIQUE_VERSION ||
    row.algorithm !== CRITIQUE_ALGORITHM ||
    typeof row.payloadHash !== 'string' ||
    !HASH.test(row.payloadHash) ||
    typeof row.signature !== 'string' ||
    !/^[A-Za-z0-9_-]+$/.test(row.signature) ||
    row.signature.length > 2_000
  ) {
    return null
  }
  const payload = critiquePayload(row.payload)
  const publicKey = jsonValue(row.publicKey)
  if (!payload || publicKey === INVALID || !object(publicKey)) return null
  return {
    version: CRITIQUE_VERSION,
    algorithm: CRITIQUE_ALGORITHM,
    payload,
    payloadHash: row.payloadHash.toLowerCase(),
    publicKey: publicKey as JsonWebKey,
    signature: row.signature,
  }
}

/* ── two records, read one at a time by a file from somebody else ──────────
   `core/mergeRequest.ts` reads a bundle another person exported, and it must be
   held to the bounds a stored document is held to. These wrap this codec's own
   validators rather than restating them: a second spelling of what a person or a
   semantic snapshot may contain would be a second boundary, and the looser of two
   boundaries is the one a hand-edited file reaches for.

   Only these two. A merge request's commit log, branch lineage and signed
   critiques are exactly a research record's, so they go through
   `sanitizeResearchState` whole and pick up its cryptographic pass with them. */

/** A person, bounded and closed like every other stored actor. */
export function readPersonActor(value: unknown): PersonActor | null {
  return personActor(value)
}

/**
 * A semantic snapshot, bounded for depth and size before its shape is checked.
 *
 * Through `snapshotJson`, not the general reader: an exploration is not a list a
 * surface renders, and truncating one to 512 keys is the one thing this reader
 * must not do quietly. The import door REFUSES; it does not drop by rule.
 */
export function readSemanticSnapshot(value: unknown): SemanticSnapshot | null {
  const parsed = snapshotJson(value)
  return parsed !== INVALID && validSemanticSnapshot(parsed) ? parsed : null
}

function validLineages(
  rows: readonly BranchLineage[],
  diagnostic: DiagnosticSink,
): BranchLineage[] {
  const pending = new Map(uniqueBy(rows, (row) => row.branchId).map((row) => [row.branchId, row]))
  const accepted = new Map<string, BranchLineage>()
  let moved = true
  while (moved) {
    moved = false
    for (const [id, row] of [...pending.entries()].sort(([a], [b]) => lexical(a, b))) {
      if (row.parentBranchId === null) {
        accepted.set(id, row)
        pending.delete(id)
        moved = true
        continue
      }
      const parent = accepted.get(row.parentBranchId)
      if (parent && row.depth === parent.depth + 1) {
        accepted.set(id, row)
        pending.delete(id)
        moved = true
      }
    }
  }
  for (const id of [...pending.keys()].sort(lexical)) {
    diagnostic({
      code: 'entry-dropped',
      path: `branchLineages.${id}`,
      message: 'Branch lineage had a missing parent, invalid depth, or cycle.',
    })
  }
  return [...accepted.values()].sort((a, b) => lexical(a.branchId, b.branchId))
}

function parsedCollection<T>(
  value: unknown,
  maximum: number,
  path: string,
  diagnostic: DiagnosticSink,
  parse: (value: unknown, index: number) => T | null,
  id: (value: T) => string,
): T[] {
  return uniqueBy(
    boundedRows(value, maximum, path, diagnostic).flatMap((raw, index) => {
      const parsed = parse(raw, index)
      if (parsed) return [parsed]
      diagnostic({
        code: 'entry-dropped',
        path: `${path}[${index}]`,
        message: 'Malformed or unsafe entry was dropped.',
      })
      return []
    }),
    id,
  )
}

interface ParsedResearchState extends StructurallySanitizedResearchState {
  state: ResearchState
}

function parseResearchState(
  value: unknown,
  options: SanitizeResearchStateOptions,
): ParsedResearchState {
  const diagnostics: ResearchStateDiagnostic[] = []
  const diagnostic: DiagnosticSink = (row) => {
    if (diagnostics.length < 10_000) diagnostics.push(row)
  }
  const now = time(options.now, Date.now())
  const fallback = (): ResearchState =>
    createResearchState({ id: options.fallbackId ?? 'research', now })
  const row = object(value)
  if (!row || row.kind !== RESEARCH_STATE_KIND) {
    diagnostic({
      code: 'invalid-root',
      path: '$',
      message: 'The input was not a Pathways research-state document.',
    })
    return {
      state: fallback(),
      diagnostics,
      accepted: false,
      cryptographicRecordsVerified: false,
    }
  }
  if (row.version !== RESEARCH_STATE_VERSION) {
    diagnostic({
      code: 'unsupported-version',
      path: 'version',
      message: `Unsupported research-state version: ${String(row.version)}.`,
    })
    return {
      state: fallback(),
      diagnostics,
      accepted: false,
      cryptographicRecordsVerified: false,
    }
  }

  const assessments = row.assessments === undefined ? null : parseRunAssessments(row.assessments)
  if (row.assessments !== undefined && !assessments) {
    diagnostic({
      code: 'invalid-root',
      path: 'assessments',
      message:
        'The assessment ledger was refused whole. Preserve the original document; unknown paid attempts cannot be discarded.',
    })
    return { state: fallback(), diagnostics, accepted: false, cryptographicRecordsVerified: false }
  }

  const knowledgeRoot = object(row.knowledgeHistory)
  if (
    Array.isArray(knowledgeRoot?.revisions) &&
    knowledgeRoot.revisions.length > RESEARCH_STATE_LIMITS.knowledgeRevisionInputs
  ) {
    diagnostic({
      code: 'limit-applied',
      path: 'knowledgeHistory.revisions',
      message: `The history exceeds the ${RESEARCH_STATE_LIMITS.knowledgeRevisionInputs}-row input limit. The document was refused whole so no chopped predecessor chain or invented omission count replaces it.`,
    })
    return {
      state: fallback(),
      diagnostics,
      accepted: false,
      cryptographicRecordsVerified: false,
    }
  }

  const id = requiredText(row.id, RESEARCH_STATE_LIMITS.idChars) ?? options.fallbackId ?? 'research'
  const createdAt = time(row.createdAt, now)
  const updatedAt = Math.max(createdAt, time(row.updatedAt, createdAt))
  const evidenceRecords = parsedCollection(
    row.evidenceRecords,
    RESEARCH_STATE_LIMITS.evidenceRecords,
    'evidenceRecords',
    diagnostic,
    (raw, index) => evidenceRecord(raw, diagnostic, `evidenceRecords[${index}]`),
    (item) => item.id,
  )
  const itemIds = new Set(evidenceRecords.map((item) => item.id))
  const evidenceById = new Map(evidenceRecords.map((item) => [item.id, item]))
  const manualEvidenceBindings = parsedCollection(
    row.manualEvidenceBindings,
    RESEARCH_STATE_LIMITS.manualEvidenceBindings,
    'manualEvidenceBindings',
    diagnostic,
    (raw) => manualEvidenceBinding(raw, evidenceById),
    (item) => item.id,
  )
  const genealogyOverrides = parsedCollection(
    row.genealogyOverrides,
    RESEARCH_STATE_LIMITS.genealogyOverrides,
    'genealogyOverrides',
    diagnostic,
    (raw) => genealogyOverride(raw, itemIds),
    (item) => item.id,
  )
  const coverageTraces = parsedCollection(
    row.coverageTraces,
    RESEARCH_STATE_LIMITS.coverageTraces,
    'coverageTraces',
    diagnostic,
    (raw, index) => coverageTrace(raw, diagnostic, `coverageTraces[${index}]`),
    (item) => item.protocol.id,
  )
  const evidenceAnnotations = parsedCollection(
    row.evidenceAnnotations,
    RESEARCH_STATE_LIMITS.evidenceAnnotations,
    'evidenceAnnotations',
    diagnostic,
    (raw) => evidenceAnnotation(raw, itemIds),
    evidenceAnnotationKey,
  )
  const claimAnnotations = parsedCollection(
    row.claimAnnotations,
    RESEARCH_STATE_LIMITS.claimAnnotations,
    'claimAnnotations',
    diagnostic,
    (raw) => claimAnnotation(raw),
    (item) => item.nodeId,
  )
  const decisionCriterionSignals = parsedCollection(
    row.decisionCriterionSignals,
    RESEARCH_STATE_LIMITS.decisionCriterionSignals,
    'decisionCriterionSignals',
    diagnostic,
    (raw) => decisionCriterionSignal(raw),
    (item) => item.id,
  )
  const claimScopes = parsedCollection(
    row.claimScopes,
    RESEARCH_STATE_LIMITS.claimScopes,
    'claimScopes',
    diagnostic,
    (raw) => researchClaimScope(raw),
    (item) => item.nodeId,
  )
  const contradictionAnalyses = parsedCollection(
    row.contradictionAnalyses,
    RESEARCH_STATE_LIMITS.contradictionAnalyses,
    'contradictionAnalyses',
    diagnostic,
    (raw) => contradictionAnalysis(raw),
    (item) => item.id,
  )
  const falsificationPortfolios = parsedCollection(
    row.falsificationPortfolios,
    RESEARCH_STATE_LIMITS.falsificationPortfolios,
    'falsificationPortfolios',
    diagnostic,
    (raw) => falsificationPortfolio(raw),
    (item) => item.id,
  )
  const calibrationTournaments = parsedCollection(
    row.calibrationTournaments,
    RESEARCH_STATE_LIMITS.calibrationTournaments,
    'calibrationTournaments',
    diagnostic,
    (raw) => calibrationTournament(raw),
    (item) => item.id,
  )
  const semanticCommits = parsedCollection(
    row.semanticCommits,
    RESEARCH_STATE_LIMITS.semanticCommits,
    'semanticCommits',
    diagnostic,
    (raw) => semanticCommit(raw),
    (item) => item.hash,
  ).sort(
    (a, b) => lexical(a.branchId, b.branchId) || a.sequence - b.sequence || lexical(a.hash, b.hash),
  )
  const branchLineages = validLineages(
    parsedCollection(
      row.branchLineages,
      RESEARCH_STATE_LIMITS.branchLineages,
      'branchLineages',
      diagnostic,
      (raw) => branchLineage(raw),
      (item) => item.branchId,
    ),
    diagnostic,
  )
  const workspace = semanticWorkspace(row.semanticWorkspace)
  if (row.semanticWorkspace !== undefined && row.semanticWorkspace !== null && !workspace) {
    diagnostic({
      code: 'entry-dropped',
      path: 'semanticWorkspace',
      message: 'Malformed semantic workspace metadata was dropped.',
    })
  }
  const signedCritiques = parsedCollection(
    row.signedCritiques,
    RESEARCH_STATE_LIMITS.signedCritiques,
    'signedCritiques',
    diagnostic,
    (raw) => signedCritique(raw),
    (item) => item.payload.critiqueId,
  )
  if (row.knowledgeHistory !== undefined && !knowledgeRoot) {
    diagnostic({
      code: 'field-defaulted',
      path: 'knowledgeHistory',
      message: 'Malformed whole-board history was replaced with an empty replay boundary.',
    })
  }
  const knowledgeRows = parsedCollection(
    knowledgeRoot?.revisions,
    RESEARCH_STATE_LIMITS.knowledgeRevisionInputs,
    'knowledgeHistory.revisions',
    diagnostic,
    (raw) => knowledgeRevision(raw),
    (item) => item.id,
  ).sort((a, b) => a.recordedAt - b.recordedAt || lexical(a.id, b.id))
  // The chain, walked in order: a delta rebuilds from the row before it and from
  // no other, and a row past a break is dropped rather than guessed at. Rows a
  // document from before D81 wrote whole come back as a chain from here.
  const resolved = resolveKnowledgeRows(knowledgeRows)
  for (const row of resolved.dropped) {
    diagnostic({
      code: 'entry-dropped',
      path: `knowledgeHistory.revisions.${row.id}`,
      message:
        'Retained state could not be rebuilt: the row it changes from is not the retained row before it.',
    })
  }
  const knowledgeHistory: KnowledgeHistory = normalizeKnowledgeHistory(
    resolved.boards,
    integer(knowledgeRoot?.omittedBefore),
    knowledgeRows,
  )
  if (resolved.boards.length > RESEARCH_STATE_LIMITS.knowledgeRevisions)
    diagnostic({
      code: 'limit-applied',
      path: 'knowledgeHistory.revisions',
      message: `Kept the newest ${RESEARCH_STATE_LIMITS.knowledgeRevisions} rebuilt states; ${resolved.boards.length - RESEARCH_STATE_LIMITS.knowledgeRevisions} earlier states are counted in omittedBefore.`,
    })
  const auditReceipts = parsedCollection(
    row.auditReceipts,
    RESEARCH_STATE_LIMITS.auditReceipts,
    'auditReceipts',
    diagnostic,
    (raw, index) => auditReceipt(raw, diagnostic, `auditReceipts[${index}]`),
    (item) => item.id,
  )

  const agenda = row.agenda === undefined ? null : parseAgenda(row.agenda)
  if (row.agenda !== undefined && !agenda)
    diagnostic({
      code: 'field-defaulted',
      path: 'agenda',
      message:
        'Malformed agenda was withheld as a whole; no attempted step was restored as pending.',
    })

  const mathematics =
    row.mathematics === undefined ? null : parseMathematicalResearch(row.mathematics)
  const mathematicsInvalid =
    row.mathematicsInvalid === true || (row.mathematics !== undefined && !mathematics)
  if (mathematicsInvalid)
    diagnostic({
      code: 'field-defaulted',
      path: 'mathematics',
      message:
        'Invalid mathematical records were withheld. Research execution remains blocked until the workspace is repaired explicitly.',
    })

  const computationalWorkspace =
    row.computationalWorkspace === undefined
      ? null
      : parseComputationalWorkspace(row.computationalWorkspace)
  const experiments =
    row.experiments === undefined ? null : parseExperimentWorkspace(row.experiments)
  if (row.experiments !== undefined && !experiments)
    diagnostic({
      code: 'field-defaulted',
      path: 'experiments',
      message:
        'Malformed experiment workspace withheld as a whole; no attempted execution was restored as new work.',
    })
  if (row.computationalWorkspace !== undefined && !computationalWorkspace)
    diagnostic({
      code: 'field-defaulted',
      path: 'computationalWorkspace',
      message: 'Malformed or oversized computational workspace was withheld as a whole.',
    })

  return {
    accepted: true,
    cryptographicRecordsVerified: false,
    diagnostics,
    state: {
      kind: RESEARCH_STATE_KIND,
      version: RESEARCH_STATE_VERSION,
      id,
      createdAt,
      updatedAt,
      evidenceProfile: profile(row.evidenceProfile, diagnostic),
      evidenceRecords,
      manualEvidenceBindings,
      genealogyOverrides,
      coverageTraces,
      evidenceAnnotations,
      claimAnnotations,
      decisionCriterionSignals,
      claimScopes,
      contradictionAnalyses,
      falsificationPortfolios,
      calibrationTournaments,
      semanticCommits,
      branchLineages,
      semanticWorkspace: workspace,
      signedCritiques,
      knowledgeHistory,
      auditReceipts,
      ...(agenda ? { agenda } : {}),
      ...(computationalWorkspace ? { computationalWorkspace } : {}),
      ...(experiments ? { experiments } : {}),
      ...(mathematics ? { mathematics } : {}),
      ...(mathematicsInvalid ? { mathematicsInvalid: true as const } : {}),
      ...(assessments ? { assessments } : {}),
    },
  }
}

/**
 * Normalize an already-typed in-memory state. Cryptographic records are cloned
 * and bounded here; unknown persistence input should use `sanitizeResearchState`
 * so their hashes and signatures are verified as well.
 */
export function normalizeResearchState(state: ResearchState): ResearchState {
  return parseResearchState(state, {
    fallbackId: state.id,
    now: state.createdAt,
  }).state
}

/**
 * Enforce the session-aware half of manual admission after the standalone
 * research codec has validated record identity. Only annotations owned by a
 * removed manual join are pruned; source annotations and unrelated binding
 * annotations remain inspectable.
 */
export function pruneManualEvidenceBindingsForNodes(
  state: ResearchState,
  validNodeIds: ReadonlySet<string>,
): ResearchState {
  const removedIds = new Set(
    state.manualEvidenceBindings
      .filter((binding) => !validNodeIds.has(binding.nodeId))
      .map((binding) => binding.id),
  )
  if (!removedIds.size) return state
  return {
    ...state,
    manualEvidenceBindings: state.manualEvidenceBindings.filter((binding) =>
      validNodeIds.has(binding.nodeId),
    ),
    evidenceAnnotations: state.evidenceAnnotations.filter(
      (annotation) => annotation.key.kind !== 'binding' || !removedIds.has(annotation.key.id),
    ),
  }
}

/**
 * Bounded synchronous persistence codec for startup paths that cannot await.
 * Structurally valid signed records are preserved, but the literal flag makes
 * it impossible to mistake this result for the authenticated async import.
 */
export function parseResearchStateSync(
  value: unknown,
  options: SanitizeResearchStateOptions = {},
): StructurallySanitizedResearchState {
  try {
    return parseResearchState(value, options)
  } catch {
    const now = time(options.now, Date.now())
    return {
      state: createResearchState({ id: options.fallbackId ?? 'research', now }),
      diagnostics: [
        {
          code: 'invalid-root',
          path: '$',
          message: 'The research-state input could not be read safely.',
        },
      ],
      accepted: false,
      cryptographicRecordsVerified: false,
    }
  }
}

interface VerifiedSemanticHistory {
  commits: SemanticCommit[]
  lineages: BranchLineage[]
}

async function verifiedSemanticHistory(
  commits: readonly SemanticCommit[],
  lineages: readonly BranchLineage[],
  diagnostic: DiagnosticSink,
): Promise<VerifiedSemanticHistory> {
  const groups = new Map<string, SemanticCommit[]>()
  for (const commit of commits) {
    const list = groups.get(commit.branchId)
    if (list) list.push(commit)
    else groups.set(commit.branchId, [commit])
  }
  const verifiedGroups = new Map<string, SemanticCommit[]>()
  for (const branchId of [...groups.keys()].sort(lexical)) {
    const history = groups
      .get(branchId)!
      .sort((a, b) => a.sequence - b.sequence || lexical(a.hash, b.hash))
    try {
      const check = await verifySemanticCommitLog(history)
      if (check.valid) {
        verifiedGroups.set(branchId, history)
      } else {
        diagnostic({
          code: 'semantic-log-rejected',
          path: `semanticCommits.${branchId}`,
          message: check.reason ?? 'Semantic commit verification failed.',
        })
      }
    } catch {
      diagnostic({
        code: 'semantic-log-rejected',
        path: `semanticCommits.${branchId}`,
        message: 'Semantic commit verification could not be completed.',
      })
    }
  }

  const acceptedCommits: SemanticCommit[] = []
  const acceptedLineages: BranchLineage[] = []
  const acceptedHashes = new Map<string, SemanticCommit>()
  const handledBranches = new Set<string>()
  for (const lineage of [...lineages].sort(
    (a, b) => a.depth - b.depth || lexical(a.branchId, b.branchId),
  )) {
    const history = verifiedGroups.get(lineage.branchId)
    let lineageValid = lineage.parentBranchId === null
    if (lineage.parentBranchId !== null) {
      const parentAccepted = acceptedLineages.some((row) => row.branchId === lineage.parentBranchId)
      const fork = lineage.forkedFromCommit
        ? acceptedHashes.get(lineage.forkedFromCommit)
        : undefined
      lineageValid = Boolean(parentAccepted && fork && fork.branchId === lineage.parentBranchId)
    }
    if (!lineageValid) {
      diagnostic({
        code: 'semantic-log-rejected',
        path: `branchLineages.${lineage.branchId}`,
        message: 'Branch fork does not name a verified revision of its parent branch.',
      })
      handledBranches.add(lineage.branchId)
      continue
    }

    acceptedLineages.push(lineage)
    if (!history) continue
    handledBranches.add(lineage.branchId)
    const expectedParent = lineage.forkedFromCommit
    if (history[0]!.parentHash !== expectedParent) {
      diagnostic({
        code: 'semantic-log-rejected',
        path: `semanticCommits.${lineage.branchId}`,
        message: 'The first branch commit is not attached to its recorded fork revision.',
      })
      continue
    }
    acceptedCommits.push(...history)
    for (const commit of history) acceptedHashes.set(commit.hash, commit)
  }
  for (const branchId of [...verifiedGroups.keys()].sort(lexical)) {
    if (handledBranches.has(branchId)) continue
    diagnostic({
      code: 'semantic-log-rejected',
      path: `semanticCommits.${branchId}`,
      message: 'Semantic commits have no valid branch lineage.',
    })
  }
  return { commits: acceptedCommits, lineages: acceptedLineages }
}

async function verifiedCritiques(
  critiques: readonly SignedCritiqueEnvelope[],
  commits: readonly SemanticCommit[],
  lineages: readonly BranchLineage[],
  diagnostic: DiagnosticSink,
): Promise<SignedCritiqueEnvelope[]> {
  const accepted: SignedCritiqueEnvelope[] = []
  for (const critique of critiques) {
    try {
      const signatureValid = await verifyCritique(critique)
      const binding = critiqueBindingCheck(critique.payload, commits, lineages)
      if (signatureValid && binding.valid) {
        accepted.push(critique)
      } else {
        diagnostic({
          code: 'critique-rejected',
          path: `signedCritiques.${critique.payload.critiqueId}`,
          message: signatureValid
            ? (binding.reason ?? 'Critique does not name a verified branch revision.')
            : 'Critique signature or payload hash did not verify.',
        })
      }
    } catch {
      diagnostic({
        code: 'critique-rejected',
        path: `signedCritiques.${critique.payload.critiqueId}`,
        message: 'Critique verification could not be completed.',
      })
    }
  }
  return accepted
}

function verifiedWorkspace(
  workspace: SemanticWorkspace | null,
  commits: readonly SemanticCommit[],
  lineages: readonly BranchLineage[],
  diagnostic: DiagnosticSink,
): SemanticWorkspace | null {
  if (!workspace) return null
  try {
    const head = semanticBranchHead(commits, lineages, workspace.checkedOutBranchId)
    if (head?.hash === workspace.checkedOutCommitHash) return workspace
  } catch {
    // The one diagnostic below deliberately covers unknown, broken, and stale heads.
  }
  diagnostic({
    code: 'semantic-log-rejected',
    path: 'semanticWorkspace',
    message: 'The checked-out revision is not the verified head of its branch.',
  })
  return null
}

/**
 * Strict persistence boundary. Unsupported roots fail wholesale; malformed
 * entries are itemized and dropped; fabricated citation admissions are demoted;
 * semantic histories and critiques survive only cryptographic verification.
 */
export async function sanitizeResearchState(
  value: unknown,
  options: SanitizeResearchStateOptions = {},
): Promise<SanitizedResearchState> {
  const parsed = parseResearchStateSync(value, options)
  if (!parsed.accepted) {
    return {
      state: parsed.state,
      diagnostics: parsed.diagnostics,
      accepted: false,
      cryptographicRecordsVerified: true,
    }
  }
  const diagnostic: DiagnosticSink = (row) => parsed.diagnostics.push(row)
  const history = await verifiedSemanticHistory(
    parsed.state.semanticCommits,
    parsed.state.branchLineages,
    diagnostic,
  )
  const semanticWorkspace = verifiedWorkspace(
    parsed.state.semanticWorkspace,
    history.commits,
    history.lineages,
    diagnostic,
  )
  const signedCritiques = await verifiedCritiques(
    parsed.state.signedCritiques,
    history.commits,
    history.lineages,
    diagnostic,
  )
  return {
    accepted: true,
    cryptographicRecordsVerified: true,
    diagnostics: parsed.diagnostics.sort(
      (a, b) => lexical(a.path, b.path) || lexical(a.code, b.code) || lexical(a.message, b.message),
    ),
    state: {
      ...parsed.state,
      semanticCommits: history.commits,
      branchLineages: history.lineages,
      semanticWorkspace,
      signedCritiques,
    },
  }
}

function entityId<K extends ResearchCollection>(
  collection: K,
  value: ResearchEntityMap[K],
): string {
  switch (collection) {
    case 'evidenceRecords':
    case 'manualEvidenceBindings':
    case 'genealogyOverrides':
    case 'contradictionAnalyses':
    case 'falsificationPortfolios':
    case 'calibrationTournaments':
    case 'auditReceipts':
      return (value as { id: string }).id
    case 'coverageTraces':
      return (value as StoredCoverageTrace).protocol.id
    case 'evidenceAnnotations':
      return evidenceAnnotationKey(value as EvidenceAnnotation)
    case 'claimAnnotations':
      return (value as ResearchClaimAnnotation).nodeId
    case 'decisionCriterionSignals':
      return (value as ResearchDecisionCriterionSignal).id
    case 'claimScopes':
      return (value as ResearchClaimScope).nodeId
    case 'semanticCommits':
      return (value as SemanticCommit).hash
    case 'branchLineages':
      return (value as BranchLineage).branchId
    case 'signedCritiques':
      return (value as SignedCritiqueEnvelope).payload.critiqueId
  }
}

/** Add or replace one collection member without retaining caller-owned references. */
export function upsertResearchEntity<K extends ResearchCollection>(
  state: ResearchState,
  collection: K,
  value: ResearchEntityMap[K],
  updatedAt = Date.now(),
): ResearchState {
  return upsertResearchEntities(state, collection, [value], updatedAt)
}

/** Batch counterpart; duplicate ids resolve canonically rather than by arrival order. */
export function upsertResearchEntities<K extends ResearchCollection>(
  state: ResearchState,
  collection: K,
  values: readonly ResearchEntityMap[K][],
  updatedAt = Date.now(),
): ResearchState {
  const incoming = values.map((value) => clone(value))
  const ids = new Set(incoming.map((value) => entityId(collection, value)))
  const current = state[collection] as unknown as readonly ResearchEntityMap[K][]
  const rows = uniqueBy(
    [...current.filter((value) => !ids.has(entityId(collection, value))), ...incoming],
    (value) => entityId(collection, value),
  )
  const next = {
    ...state,
    [collection]: rows,
    updatedAt: Math.max(state.updatedAt, time(updatedAt, state.updatedAt)),
  } as ResearchState
  return normalizeResearchState(next)
}

/** Deterministic evidence-family projection over the records and overrides held here. */
export function researchGenealogy(state: ResearchState) {
  return buildEvidenceGenealogy(state.evidenceRecords, state.genealogyOverrides)
}
